import { z } from 'zod';
import { romaniaClock, romaniaInstant } from '../romania-calendar';
import type { TenderOperationCode } from './project-organization';
import { projectStatusEnum } from './projects';

/**
 * Which projects may be put on the board.
 *
 * A rejected project is not going to be built, so offering it for scheduling is
 * only ever a mis-click waiting to happen. A project already IN_WARRANTY has
 * finished its production run — the board still carries it, in the warranty
 * section, and enrolment would be refused as a duplicate PRODUCTION row. An
 * ARCHIVED one is read-only outright.
 *
 * Derived by subtraction rather than listed, so a status added later is
 * schedulable until someone decides otherwise — the failure mode of the
 * opposite default is a project silently missing from the picker with nothing
 * to explain why.
 */
export const schedulableProjectStatuses = projectStatusEnum.options.filter(
  (status) => status !== 'REJECTED' && status !== 'IN_WARRANTY' && status !== 'ARCHIVED',
);

/**
 * Operations Scheduler — simplified, team-focused production pipeline.
 *
 * Every enrolled project moves through the fixed stage sequence below.
 * Each stage depends on one operation before it — by default the previous one,
 * or one picked by hand (`dependsOnStageCode`, see `schedulerDependencyIndex`)
 * — and waits for it to finish, UNLESS it has a start lag (`startLagMinutes`):
 * then it may start that long after that operation STARTED, on its own crew,
 * overlapping it. Painting is the exception to "the previous one": it runs as
 * a parallel branch (see the WORKSHOP RULES below `schedulerStageCodes`). Queue
 * order is a composite priority score — deadline pressure dominates, then
 * urgency, then importance, then FIFO seniority — and deadline-critical
 * projects may preempt a running stage. Work is measured in Int minutes
 * against a 7h/day work calendar (every day counts); a virtual clock lets the
 * whole pipeline be fast-forwarded in simulation.
 * Stage codes reuse the Operations Planner phase codes minus PROCUREMENT,
 * plus PAINTING ("Vopsitorie"), which the Planner has no equivalent for.
 * A finished project stays under warranty 24 months; a warranty intervention
 * is a separate scheduler row (`kind: 'WARRANTY'`).
 */

/**
 * THE PIPELINE ORDER. Array position is the order — there is no separate order
 * table — and at enrollment the index is copied verbatim into
 * `SchedulerProjectStage.orderIndex`, which every runtime sequencing query then
 * reads instead of the code string.
 *
 * So REORDERING OR INSERTING A CODE HERE IS A DATA MIGRATION, not an edit:
 * `moveProjectStart` compares the stored `orderIndex` against this array's live
 * `indexOf`, so already-enrolled projects must be renumbered to match, and each
 * needs a row for the new stage (a missing row makes estimate updates silently
 * no-op). Backfilled rows must be DONE with `completedAt: null` — the
 * "skipped" encoding — because a not-DONE stage with no team marks every
 * project blocked.
 *
 * `orderIndex` remains the PIPELINE order and the queue tiebreak, and starts
 * still follow it. Finishes no longer do: with a start lag a later operation
 * can overlap an earlier one and finish first. Anything asking "what may start
 * now" must go through `engine/types.ts` `stageRelease`, never through
 * `orderIndex + 1`.
 */
export const schedulerStageCodes = [
  'DESIGN',
  'CUTTING',
  'EDGING',
  'CNC',
  'PAINTING',
  'ASD',
  'INSTALLATION',
] as const;
export const schedulerStageCodeEnum = z.enum(schedulerStageCodes);

/**
 * INTERNAL crews clock in, earn overtime and share the punctuality bonus.
 * COLLABORATOR crews are external and paid per project: no roster, no
 * attendance, no bonus — the scheduler treats them purely as capacity.
 */
export const schedulerTeamKindEnum = z.enum(['INTERNAL', 'COLLABORATOR']);
export type SchedulerTeamKindValue = z.infer<typeof schedulerTeamKindEnum>;
export type SchedulerStageCode = z.infer<typeof schedulerStageCodeEnum>;

/**
 * Which stage performs each operation Project Organization costs.
 *
 * The two vocabularies describe the same shop floor, and each keeps its own
 * language: the tender operations are Romanian (Circular, Cant, Vopsitorie),
 * the stages English (CUTTING, EDGING, PAINTING), bridged here.
 *
 * Every stage that consumes costed labour now has an entry. DESIGN used to be
 * the deliberate hole — "the tender prices manufacturing operations only" — but
 * Proiectare is a costed operation now, so a project's design estimate IS
 * filled in from the costing. Note the consequence: `tenderDistribution`
 * overwrites the DESIGN estimate, which previously kept a hard-coded default,
 * so a project whose Proiectare hours are 0 gets a 0 design estimate.
 */
export const schedulerStageForTenderOperation: Record<TenderOperationCode, SchedulerStageCode> = {
  CIRCULAR: 'CUTTING',
  CANT: 'EDGING',
  CNC: 'CNC',
  VOPSITORIE: 'PAINTING',
  ASAMBLARE: 'ASD',
  MONTAJ: 'INSTALLATION',
  PROIECTARE: 'DESIGN',
};

/**
 * WORKSHOP RULES — how the operations depend on one another, whatever a manager
 * types into a project. The engine (settle, projection, scoring, the queue sync)
 * and the web's estimated plan all read the pipeline through the helpers below,
 * so the board, the Gantt and the dispatcher cannot disagree about them.
 *
 * 1. Cantuire (EDGING) and CNC may start no sooner than 2h after the operation
 *    before them STARTED. It is a floor on their start lag and the lag they are
 *    enrolled with by default; "wait for it to finish" stays allowed, being
 *    stricter. As with every lag, a predecessor that finishes sooner releases
 *    them at its finish.
 * 2. Vopsire (PAINTING) is a parallel branch: the operations after it do not
 *    wait for it — Asamblare follows CNC directly. It starts once the operation
 *    before it (CNC) FINISHES, unless it is given a start lag or another
 *    operation to count from, like any other operation. Instead of being
 *    waited on, painting gates Montaj (INSTALLATION): Montaj may not start
 *    until painting has at most HALF of the installation's duration left, so
 *    painting is done by the time installation is half-way through.
 */
export const schedulerMinStartLagMinutes: Partial<Record<SchedulerStageCode, number>> = {
  EDGING: 120,
  CNC: 120,
};

/** The operation run alongside the rest of the pipeline (rule 2). */
export const SCHEDULER_PARALLEL_STAGE_CODE: SchedulerStageCode = 'PAINTING';
/** The operation the parallel one must be finished by the half-way point of. */
export const SCHEDULER_PARALLEL_DEADLINE_STAGE_CODE: SchedulerStageCode = 'INSTALLATION';

/**
 * The start lag the rules actually allow for an operation: never less than the
 * floor where there is one. Null keeps meaning "after the operation it depends
 * on finishes".
 */
export function schedulerEffectiveStartLagMinutes(
  stageCode: string,
  lagMinutes: number | null,
): number | null {
  if (lagMinutes === null) return null;
  const floor = schedulerMinStartLagMinutes[stageCode as SchedulerStageCode];
  return floor === undefined ? lagMinutes : Math.max(lagMinutes, floor);
}

/**
 * The position of the operation whose start/finish releases `codes[index]`, in
 * a pipeline given as the codes of the operations a project actually goes
 * through, in order. Null when it heads the pipeline.
 *
 * Ordinarily the entry before it — except right after the parallel operation,
 * which is stepped over: the operation after painting follows what painting
 * follows. Only when painting HEADS the pipeline (a project arriving at the
 * paint shop) is it kept as the predecessor, so a pipeline always has exactly
 * one head — which is what enrollment, deferral holds and the dispatcher assume.
 */
export function schedulerPredecessorIndex(codes: readonly string[], index: number): number | null {
  if (index <= 0) return null;
  if (codes[index - 1] === SCHEDULER_PARALLEL_STAGE_CODE && index >= 2) return index - 2;
  return index - 1;
}

/**
 * The position of the operation `codes[index]` DEPENDS on: its start lag counts
 * from that operation's start, and with no lag it waits for that operation to
 * finish. Null when it heads the pipeline.
 *
 * Ordinarily the workshop rule ({@link schedulerPredecessorIndex}). A dependency
 * picked by hand (`dependsOn`, "Începe după … de la start …") wins while that
 * operation is in the pipeline BEFORE this one — the order itself never
 * changes, so a project keeps exactly one head. One that is not (left out, or
 * skipped by the start) falls back to the rule.
 */
export function schedulerDependencyIndex(
  codes: readonly string[],
  index: number,
  dependsOn?: string | null,
): number | null {
  if (index <= 0) return null;
  if (dependsOn) {
    const at = codes.indexOf(dependsOn);
    if (at >= 0 && at < index) return at;
  }
  return schedulerPredecessorIndex(codes, index);
}

/**
 * The position of the parallel operation `codes[index]` must additionally wait
 * for (rule 2), or null. Only the deadline operation is gated, and only while
 * painting is a genuine side branch — when painting is what it depends on
 * (`dependsOn`, see {@link schedulerDependencyIndex}) it waits for painting to
 * finish anyway.
 */
export function schedulerParallelGateIndex(
  codes: readonly string[],
  index: number,
  dependsOn?: string | null,
): number | null {
  if (codes[index] !== SCHEDULER_PARALLEL_DEADLINE_STAGE_CODE) return null;
  const predecessor = schedulerDependencyIndex(codes, index, dependsOn);
  if (predecessor === null || codes[predecessor] === SCHEDULER_PARALLEL_STAGE_CODE) return null;
  const parallel = codes.indexOf(SCHEDULER_PARALLEL_STAGE_CODE);
  return parallel >= 0 && parallel < index ? parallel : null;
}

/**
 * How many work-minutes into painting's remaining run the gated operation may
 * start: once painting has no more than half of `deadlineStageMinutes` left.
 * Zero when that is already the case.
 */
export function schedulerParallelGateOffsetMinutes(
  parallelRemainingMinutes: number,
  deadlineStageMinutes: number,
): number {
  return Math.max(0, parallelRemainingMinutes - Math.floor(Math.max(0, deadlineStageMinutes) / 2));
}

/**
 * Team-name prefixes per stage: P1, P2… / C1… / Cant1… / CNC1… / V1… / A1… / M1…
 *
 * These must stay mutually unambiguous: `nextTeamName` scans every existing
 * team name against `^<prefix>(\d+)$`, so a prefix that is a prefix of another
 * would capture the wrong numbers.
 */
export const schedulerTeamPrefixes: Record<SchedulerStageCode, string> = {
  DESIGN: 'P',
  CUTTING: 'C',
  EDGING: 'Cant',
  CNC: 'CNC',
  PAINTING: 'V',
  ASD: 'A',
  INSTALLATION: 'M',
};

/**
 * The scheduler's ORIGINAL day: 7 hours, 08:00–15:00, every day of the week.
 * Still what a work day is until the shop saves its own program ("Zile și
 * program de lucru", `schedulerCalendarDto`); from then on a day is the general
 * program's length — see `schedulerDayMinutes`.
 */
export const WORK_MINUTES_PER_DAY = 420;

/** A calendar day, as a UTC `YYYY-MM-DD` string. */
const schedulerCalendarDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD.');

/**
 * The GENERAL overtime availability ("Zile și program de lucru"): the weekdays
 * on which employees can stay for overtime, and one window for all of them.
 * Like the general program it is a template — given to the employees picked
 * when it is saved, each keeping it outside their own program — and nothing
 * the flow runs on.
 */
export const schedulerOvertimeTemplateDto = z.object({
  /** 0 = Sunday … 6 = Saturday. */
  weekdays: z.array(z.number().int().min(0).max(6)).min(1).max(7),
  startMinutes: z.number().int().min(0).max(1440),
  endMinutes: z.number().int().min(0).max(1440),
});
export type SchedulerOvertimeTemplateDto = z.infer<typeof schedulerOvertimeTemplateDto>;

/**
 * The shop's working calendar ("Zile și program de lucru"), kept on the server.
 *
 * - The GENERAL program — `startMinutes`–`endMinutes` (minutes from 00:00, in
 *   the app's UTC frame like the workers' own programs) on every day, or on
 *   weekdays only with `weekendsOff` — is what a crew with nobody on its roster
 *   works, what every crew works on an extra working date, and what "one work
 *   day" means wherever the scheduler counts in days.
 * - `nonWorkingDates` close the shop for everyone (holidays); `workingDates`
 *   open it on the general program (a working Saturday).
 * - `followWorkerSchedules`: set by the first save. From then on each crew works
 *   its members' programs (Pontaj lunar → Angajați). Before it, every crew works the
 *   general program, whose defaults are the scheduler's original window —
 *   08:00–15:00, every day — so nothing moved until the shop chose.
 */
export const schedulerCalendarDto = z.object({
  startMinutes: z.number().int(),
  endMinutes: z.number().int(),
  weekendsOff: z.boolean(),
  nonWorkingDates: z.array(schedulerCalendarDay),
  workingDates: z.array(schedulerCalendarDay),
  followWorkerSchedules: z.boolean(),
  /**
   * The general overtime availability, null when none was set. Optional only
   * so older fixtures stay valid; the API always sends it.
   */
  overtime: schedulerOvertimeTemplateDto.nullable().optional(),
});
export type SchedulerCalendarDto = z.infer<typeof schedulerCalendarDto>;

/** The calendar the scheduler starts from, and the one it keeps until first saved. */
export const SCHEDULER_LEGACY_CALENDAR: SchedulerCalendarDto = Object.freeze({
  startMinutes: 8 * 60,
  endMinutes: 8 * 60 + WORK_MINUTES_PER_DAY,
  weekendsOff: false,
  nonWorkingDates: [],
  workingDates: [],
  followWorkerSchedules: false,
  overtime: null,
}) as SchedulerCalendarDto;

/** The shortest general program accepted: an hour (a day is what thresholds count in). */
export const SCHEDULER_CALENDAR_MIN_DAY_MINUTES = 60;

/** Minutes in one work day of the general program. */
export function schedulerDayMinutes(
  calendar: Pick<SchedulerCalendarDto, 'startMinutes' | 'endMinutes'>,
): number {
  return calendar.endMinutes - calendar.startMinutes;
}

/**
 * The general overtime window overlaps the general program on a day both
 * apply to — Monday to Friday with the weekend off, else every day.
 */
export function overtimeTemplateClashesProgram(calendar: {
  startMinutes: number;
  endMinutes: number;
  weekendsOff: boolean;
  overtime: SchedulerOvertimeTemplateDto;
}): boolean {
  const { overtime } = calendar;
  const onProgramDay = overtime.weekdays.some(
    (weekday) => !calendar.weekendsOff || (weekday >= 1 && weekday <= 5),
  );
  return (
    onProgramDay &&
    overtime.startMinutes < calendar.endMinutes &&
    overtime.endMinutes > calendar.startMinutes
  );
}

/**
 * Saving the calendar ("Zile și program de lucru"). The first save also turns
 * `followWorkerSchedules` on. `applyToWorkerIds` names the workers whose weekly
 * program is replaced by the general one — the days (Monday to Friday with
 * `weekendsOff`, else every day) and the hours; [] changes nobody's.
 *
 * `overtime` sets the general overtime availability (null clears it, omitted
 * leaves it as it is); `applyOvertimeToWorkerIds` names the workers whose
 * availability is replaced by it — each window kept outside that worker's
 * program, the program winning where they overlap.
 */
export const updateSchedulerCalendarRequest = z
  .object({
    startMinutes: z.number().int().min(0).max(1440),
    endMinutes: z.number().int().min(0).max(1440),
    weekendsOff: z.boolean(),
    nonWorkingDates: z.array(schedulerCalendarDay).max(500),
    workingDates: z.array(schedulerCalendarDay).max(500),
    applyToWorkerIds: z.array(z.string().min(1)).max(500).default([]),
    overtime: schedulerOvertimeTemplateDto.nullable().optional(),
    applyOvertimeToWorkerIds: z.array(z.string().min(1)).max(500).default([]),
  })
  // Explicit: .refine() wraps this in ZodEffects, which the global pipe does
  // not strict-upgrade.
  .strict()
  .refine((v) => v.endMinutes - v.startMinutes >= SCHEDULER_CALENDAR_MIN_DAY_MINUTES, {
    path: ['endMinutes'],
    message: `The program must end at least ${SCHEDULER_CALENDAR_MIN_DAY_MINUTES} minutes after it starts.`,
  })
  .refine((v) => !v.workingDates.some((date) => v.nonWorkingDates.includes(date)), {
    path: ['workingDates'],
    message: 'A date cannot be both working and non-working.',
  })
  .refine((v) => !v.overtime || v.overtime.endMinutes > v.overtime.startMinutes, {
    path: ['overtime'],
    message: 'The overtime window must end after it starts.',
  })
  .refine((v) => !v.overtime || !overtimeTemplateClashesProgram({ ...v, overtime: v.overtime }), {
    path: ['overtime'],
    message: 'The overtime window must lie outside the general program.',
  });
export type UpdateSchedulerCalendarRequest = z.infer<typeof updateSchedulerCalendarRequest>;
export type UpdateSchedulerCalendarRequestInput = z.input<typeof updateSchedulerCalendarRequest>;

/**
 * "Fluxul numără singur orele": whether the flow counts a running operation's
 * work by itself, on its crews' programs, as time passes. Off, an operation's
 * "Ore lucrate" — and with it its progress, delay and punctuality bonus — grows
 * only from the timesheet (Pontaj, whole hours) and hand corrections, the team
 * hour counters stand still, and what is left is laid out from now. What the
 * flow counted before `offSince` stays. Start lags and crews back from leave
 * still start work on their own, at their time.
 */
export const schedulerAutoProgressDto = z.object({
  enabled: z.boolean(),
  /** When it was turned off; null while it is on. */
  offSince: z.string().datetime().nullable(),
});
export type SchedulerAutoProgressDto = z.infer<typeof schedulerAutoProgressDto>;

/**
 * Turning it off ends every running operation's automatic count at that
 * instant; turning it back on counts from then on, never the time it was off.
 */
export const updateSchedulerAutoProgressRequest = z.object({ enabled: z.boolean() }).strict();
export type UpdateSchedulerAutoProgressRequest = z.infer<typeof updateSchedulerAutoProgressRequest>;

/**
 * "Schimbul 2" on one operation: extra work-minutes per day every INTERNAL crew
 * on it works beyond its day, until the operation is DONE.
 *
 * Deliberately NOT a longer window: a crew's day stays what its program says
 * (the priority bands and overload capacity lean on one day's length), so a
 * shift is modelled as the crew getting more done inside that day — (day + m) /
 * day as fast, `schedulerSecondShiftFactor`. Dates come out right to the day;
 * hours within the day are nominal.
 *
 * 4h is the least a second shift is worth organising, 8h the most one crew can
 * add on top of its day, and the half-hour grid is what the form offers. The
 * same bounds are a CHECK on `SchedulerProjectStage.secondShiftMinutes`.
 *
 * Unrelated to the SECOND_SHIFT employment type, which only names a payroll
 * contract.
 */
export const SCHEDULER_SECOND_SHIFT_MIN_MINUTES = 240;
export const SCHEDULER_SECOND_SHIFT_MAX_MINUTES = 480;
export const SCHEDULER_SECOND_SHIFT_STEP_MINUTES = 30;
export const schedulerSecondShiftMinutes = z
  .number()
  .int()
  .min(SCHEDULER_SECOND_SHIFT_MIN_MINUTES)
  .max(SCHEDULER_SECOND_SHIFT_MAX_MINUTES)
  .multipleOf(SCHEDULER_SECOND_SHIFT_STEP_MINUTES);

/**
 * How much faster one INTERNAL crew works an operation with a second shift of
 * `minutes` a day: 1 without one. Collaborators take no shift — callers apply
 * the factor per crew, internal ones only.
 *
 * `dayMinutes` is one work day of the general program (`schedulerDayMinutes`),
 * so the shift always adds exactly `minutes` a day: (day + m) / day.
 */
export function schedulerSecondShiftFactor(
  minutes: number | null | undefined,
  dayMinutes: number = WORK_MINUTES_PER_DAY,
): number {
  return minutes ? (dayMinutes + minutes) / dayMinutes : 1;
}

/**
 * The operations an external collaborator may be brought in for — Proiectare,
 * Asamblare and Montaj. The shop's own machines (Circular, Cantuire, CNC) and
 * the paint shop stay in-house, so the deadline rescue never proposes a
 * collaborator anywhere else.
 */
export const schedulerCollaboratorStageCodes = [
  'DESIGN',
  'ASD',
  'INSTALLATION',
] as const satisfies readonly SchedulerStageCode[];
export const schedulerCollaboratorStageCodeEnum = z.enum(schedulerCollaboratorStageCodes);
export type SchedulerCollaboratorStageCode = z.infer<typeof schedulerCollaboratorStageCodeEnum>;

export const schedulerProjectStatusEnum = z.enum(['ACTIVE', 'COMPLETED', 'CANCELLED']);
export type SchedulerProjectStatusValue = z.infer<typeof schedulerProjectStatusEnum>;

export const schedulerStageStatusEnum = z.enum(['PENDING', 'QUEUED', 'IN_PROGRESS', 'DONE']);
export type SchedulerStageStatusValue = z.infer<typeof schedulerStageStatusEnum>;

export const schedulerPriorityLevels = ['VERY_LOW', 'LOW', 'NORMAL', 'HIGH', 'VERY_HIGH'] as const;
export const schedulerPriorityLevelEnum = z.enum(schedulerPriorityLevels);
export type SchedulerPriorityLevelValue = z.infer<typeof schedulerPriorityLevelEnum>;

/** Deadline pressure band, derived from slack (work minutes to deadline minus remaining work). */
export const schedulerDeadlineBandEnum = z.enum([
  'NONE',
  'COMFORTABLE',
  'WATCH',
  'TIGHT',
  'CRITICAL',
  'OVERDUE',
]);
export type SchedulerDeadlineBandValue = z.infer<typeof schedulerDeadlineBandEnum>;

/**
 * The most hours one operation can carry. Enrollment enforces it through
 * `estimateHours`, and a simulated project is held to the same bound so it can
 * never describe a project that could not really be put on the board — nor
 * push the work calendar past its search guard.
 */
export const SCHEDULER_MAX_STAGE_HOURS = 2000;

/** Hours estimate for one operation; decimals allowed, stored as Int minutes server-side. */
const estimateHours = z.number().min(0).max(SCHEDULER_MAX_STAGE_HOURS);

/** Estimated hours for every operation in the pipeline (all of them required). */
// Built from the code tuple rather than a hand-written literal: every key is
// REQUIRED here, so a stage added to the tuple but forgotten in this object
// would be rejected as an unknown key on enroll and silently dropped on update.
export const schedulerStageEstimates = z.object(
  Object.fromEntries(schedulerStageCodes.map((code) => [code, estimateHours])) as Record<
    SchedulerStageCode,
    typeof estimateHours
  >,
);
export type SchedulerStageEstimates = z.infer<typeof schedulerStageEstimates>;

/**
 * Hours already spent on an operation that never went through the clock.
 *
 * Work arriving part-finished is normal — panels cut at another shop, a design
 * done before the project was put on the board — and those hours are real even
 * though no session records them. They are seeded onto the stage's
 * `workedMinutes`, which is what the projection subtracts from the estimate, so
 * the schedule reflects the work that is genuinely left.
 *
 * Partial: an operation nobody has touched simply says nothing, and every key
 * missing (the default) is the ordinary "this project starts from scratch".
 */
export const schedulerStageWorkedHours = schedulerStageEstimates.partial();
export type SchedulerStageWorkedHours = z.infer<typeof schedulerStageWorkedHours>;

// --------------------------------------------------------------------------
// Warranty ("Proiecte în garanție")
// --------------------------------------------------------------------------
export const schedulerProjectKindEnum = z.enum(['PRODUCTION', 'WARRANTY']);
export type SchedulerProjectKindValue = z.infer<typeof schedulerProjectKindEnum>;

/** A finished project is under warranty this many calendar months from its FIRST completion. */
export const SCHEDULER_WARRANTY_MONTHS = 24;

/**
 * start + 24 calendar months on Romania's calendar, the day clamped to the
 * month's end (2024-02-29 → 2026-02-28) and Romania's time of day kept — a
 * project finished just past midnight there is still dated by that day.
 */
export function schedulerWarrantyEndsAt(startedAt: Date | string): Date {
  const startMs = new Date(startedAt).getTime();
  const { date, minutes } = romaniaClock(startMs);
  const [startYear, startMonth, startDay] = date.split('-').map(Number) as [number, number, number];
  const monthIndex = startMonth - 1 + SCHEDULER_WARRANTY_MONTHS;
  const year = startYear + Math.floor(monthIndex / 12);
  const month = monthIndex % 12;
  // Calendar arithmetic on the date alone: the day count of the month it lands in.
  const lastDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  const endDate = `${year}-${String(month + 1).padStart(2, '0')}-${String(
    Math.min(startDay, lastDay),
  ).padStart(2, '0')}`;
  // Romania's offsets are whole minutes: the seconds and milliseconds carry over as they are.
  const withinMinute = ((startMs % 60_000) + 60_000) % 60_000;
  return new Date(romaniaInstant(endDate, minutes) + withinMinute);
}

/** Still under warranty at `now` (inclusive of the end instant). */
export function isSchedulerWarrantyActive(startedAt: Date | string, now: Date | string): boolean {
  return new Date(now).getTime() <= schedulerWarrantyEndsAt(startedAt).getTime();
}

/** DB pre-filter bound: every warranty still active at `now` started after this (745 days ≥ 24 months + slack). */
export function schedulerWarrantyCutoff(now: Date | string): Date {
  return new Date(new Date(now).getTime() - 745 * 24 * 60 * 60 * 1000);
}

export const schedulerNotInWarrantyReasons = ['NOT_COMPLETED', 'NOT_PRODUCTION'] as const;
export type SchedulerNotInWarrantyReason = (typeof schedulerNotInWarrantyReasons)[number];
export const schedulerInterventionLockedReasons = [
  'START_STAGE',
  'REINCLUDE_STAGE',
  'NOT_INTERVENTION',
  'IN_WARRANTY',
  'USE_CANCEL',
  'START_AFTER_WARRANTY',
] as const;
export type SchedulerInterventionLockedReason = (typeof schedulerInterventionLockedReasons)[number];

/** A production run's warranty, shown on the production row AND on its interventions. */
export const schedulerWarrantyDto = z.object({
  startedAt: z.string().datetime(),
  endsAt: z.string().datetime(),
  /** board now <= endsAt (virtual clock). */
  active: z.boolean(),
  /** Interventions already closed (COMPLETED or CANCELLED). */
  closedInterventions: z.number().int().nonnegative(),
  /** The intervention in progress, if any (E4: at most one). */
  openIntervention: z
    .object({ id: z.string().cuid(), number: z.number().int().positive() })
    .nullable(),
});
export type SchedulerWarrantyDto = z.infer<typeof schedulerWarrantyDto>;

export const schedulerStageDto = z.object({
  id: z.string().cuid(),
  schedulerProjectId: z.string().cuid(),
  projectId: z.string().cuid(),
  stageCode: schedulerStageCodeEnum.or(z.string()),
  orderIndex: z.number().int(),
  status: schedulerStageStatusEnum,
  /** The dispatcher-assigned lead team. Also the one a preemption takes. */
  teamId: z.string().cuid().nullable(),
  teamName: z.string().nullable(),
  /**
   * The crews this operation is RESERVED for, in the order they were
   * designated — when the project was put on the board, or later from
   * "Editează programarea", for either shift (the crew's own `shift`).
   *
   * A firm reservation of the set, not a preference: while there is any, the
   * operation is offered to these crews alone and waits for one of them even
   * when another capable crew is free; each of them comes onto it as soon as
   * it is free of what it is on. Empty is the ordinary case — any crew
   * covering the operation may take it. Do not confuse it with `teams`, who
   * is actually on the operation right now.
   */
  designatedTeams: z.array(z.object({ id: z.string().cuid(), name: z.string() })),
  /**
   * Every team on this stage — the lead first, then the reinforcements a manager
   * added. Work accrues once per team per work-minute, so `teams.length` is the
   * stage's speed multiplier: 2 teams finish a 7h operation in 3h30m. Empty when
   * the stage has no team at all.
   */
  teams: z.array(z.object({ id: z.string().cuid(), name: z.string() })),
  estimatedMinutes: z.number().int(),
  /**
   * Effective worked minutes at board time (settled + live run segment) — for
   * display, so a RUNNING operation past its estimate reads as the estimate
   * (the overrun is shown on its own).
   */
  workedMinutes: z.number().int(),
  /**
   * The same figure without that cap: what "Ore lucrate" shows and edits.
   * Differs from `workedMinutes` only on a running operation past its estimate.
   */
  actualWorkedMinutes: z.number().int().optional(),
  queuedAt: z.string().datetime().nullable(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  /**
   * Set while this project does not undergo this operation at all.
   *
   * Such a stage is stored DONE, so without this flag the board could not tell
   * "we do not paint this job" from "painting is finished" — the two look
   * identical in `status`, and only one of them should be shown as an
   * accomplishment or counted toward progress.
   */
  excludedAt: z.string().datetime().nullable(),
  /**
   * How many work-minutes after the previous operation STARTED this one may
   * start, capped at that operation's finish; null means it waits for the
   * previous operation to finish. A lag shorter than the previous operation
   * makes the two overlap, which the Gantt hatches in both colours.
   */
  startLagMinutes: z.number().int().nonnegative().nullable(),
  /**
   * The operation this one depends on, picked by hand ("de la start …"): its
   * lag counts from that operation's start, and with no lag it waits for that
   * operation to finish. Null for the workshop rule — the previous operation
   * (see `schedulerDependencyIndex`, which also says when a pick does not
   * apply). Optional only so older fixtures stay valid; the API always sends it.
   */
  dependsOnStageCode: schedulerStageCodeEnum.nullable().optional(),
  /** Manual "put on waiting": set while the dispatcher must skip this stage. */
  heldAt: z.string().datetime().nullable(),
  projectedStartAt: z.string().datetime().nullable(),
  projectedFinishAt: z.string().datetime().nullable(),
  /**
   * The team the projection expects to run this stage — for QUEUED/PENDING
   * stages, where `teamName` is still null because nothing was dispatched yet.
   * Equals the assigned team once the stage runs; null when the operation has
   * no team at all.
   */
  projectedTeamName: z.string().nullable(),
  /**
   * "Schimbul 2": extra work-minutes per day the INTERNAL crews on this
   * operation work, until it is DONE (see `schedulerSecondShiftMinutes`). Null
   * = none. Optional only so older fixtures stay valid; the API always sends it.
   */
  secondShiftMinutes: z.number().int().nullable().optional(),
  /**
   * Crews confirmed to work this operation beyond its lead, in the order they
   * were planned. `reserved` marks a collaborator RESERVED for it until it is
   * done (it takes no other work); the others join — or lead — as soon as they
   * are free, and drop off this list the moment they are put on it.
   */
  plannedTeams: z
    .array(z.object({ id: z.string().cuid(), name: z.string(), reserved: z.boolean() }))
    .optional(),
  /**
   * Every crew the projection expects on this operation, the lead first, each
   * with when it starts on it — how the Gantt says "A1 + A3 (A3 se alătură
   * ~23 sept.)". Empty while the operation cannot be projected.
   */
  projectedTeams: z
    .array(z.object({ id: z.string().cuid(), name: z.string(), joinsAt: z.string().datetime() }))
    .optional(),
});
export type SchedulerStageDto = z.infer<typeof schedulerStageDto>;

export const schedulerProjectDto = z.object({
  id: z.string().cuid(),
  projectId: z.string().cuid(),
  projectCode: z.string(),
  projectTitle: z.string(),
  clientName: z.string(),
  /** PRODUCTION = the project's run through the pipeline; WARRANTY = a later intervention on it. */
  kind: schedulerProjectKindEnum,
  /** WARRANTY rows: the finished PRODUCTION row this intervention reopens. */
  warrantyOfId: z.string().cuid().nullable(),
  /** WARRANTY rows: 1, 2, … per project ("Intervenția #n"). */
  interventionNumber: z.number().int().positive().nullable(),
  /** The production run's warranty (also carried by its interventions); null until it first finished. */
  warranty: schedulerWarrantyDto.nullable(),
  status: schedulerProjectStatusEnum,
  importance: schedulerPriorityLevelEnum,
  urgency: schedulerPriorityLevelEnum,
  /**
   * The deadline scoring uses: `Project.deadline` for PRODUCTION rows, the
   * intervention's own optional deadline for WARRANTY rows (the original
   * deadline never applies to an intervention).
   */
  deadline: z.string().datetime().nullable(),
  deadlineBand: schedulerDeadlineBandEnum,
  /** Work minutes of breathing room before the deadline (negative = overdue); null without deadline. */
  slackMinutes: z.number().int().nullable(),
  priorityScore: z.number().int(),
  /** Code of the first stage that is not DONE; null once the pipeline finished. */
  currentStageCode: schedulerStageCodeEnum.or(z.string()).nullable(),
  projectedFinishAt: z.string().datetime().nullable(),
  /**
   * Manual estimated start ("Începere estimată") set on the board. Purely a
   * planning input: it never changes dispatch or queue order. The estimated
   * finish shown in the UI is derived from it (start + remaining estimated work
   * over working days) and is not stored. Null when unset.
   */
  plannedStartAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  stages: z.array(schedulerStageDto),
});
export type SchedulerProjectDto = z.infer<typeof schedulerProjectDto>;

export const schedulerTeamAssignmentDto = z.object({
  stageId: z.string().cuid(),
  projectId: z.string().cuid(),
  projectCode: z.string(),
  projectTitle: z.string(),
  clientName: z.string(),
  startedAt: z.string().datetime().nullable(),
  estimatedMinutes: z.number().int(),
  /** Worked minutes for the progress bar, capped at the estimate. */
  workedMinutes: z.number().int(),
  /**
   * Minutes worked past the estimate (0 while on time). Positive once the stage
   * runs over — the board turns its progress bar red and shows this as delay.
   */
  overdueMinutes: z.number().int(),
  projectedFinishAt: z.string().datetime().nullable(),
  /**
   * True when this team is the stage's lead. False means it is reinforcing
   * someone else's stage — it is genuinely busy, but the lead's card is where
   * the stage's teams are managed.
   */
  isLeadTeam: z.boolean(),
  /** Teams on this stage, i.e. how fast it is progressing (1 = normal speed). */
  teamCount: z.number().int(),
  /** The stage's "Schimbul 2" in extra minutes per day; null = none. */
  secondShiftMinutes: z.number().int().nullable().optional(),
});
export type SchedulerTeamAssignmentDto = z.infer<typeof schedulerTeamAssignmentDto>;

export const schedulerTeamDto = z.object({
  id: z.string().cuid(),
  kind: schedulerTeamKindEnum,
  /**
   * "În așteptare": free, and taking no new work by itself until resumed or handed
   * some. Optional only for older fixtures; the API always sends it.
   */
  waitingSince: z.string().datetime().nullable().optional(),
  /** The team's home operation — what it is named after and grouped under. */
  stageCode: schedulerStageCodeEnum.or(z.string()),
  /**
   * Other operations the same crew also covers (e.g. an Asamblare team that
   * also does Montaj). The dispatcher may hand it work from these, and because
   * a team is one row it can still only be on one operation at a time.
   */
  extraStageCodes: z.array(schedulerStageCodeEnum).default([]),
  name: z.string(),
  active: z.boolean(),
  /**
   * The shift the crew works: 2 when every one of its active members is a
   * second-shift employee (SECOND_SHIFT, an evening program), else 1 — a
   * collaborator and a crew with nobody on it included. What the operation
   * pickers group crews by; the crew works whatever its members' programs say.
   * Optional only for older fixtures (read as 1); the API always sends it.
   */
  shift: z.union([z.literal(1), z.literal(2)]).optional(),
  /**
   * Punctuality bonus: minutes finished ahead of estimate, accumulated over
   * time. Negative when stages were completed late.
   */
  bonusMinutes: z.number().int(),
  /** Cumulative minutes spent working on assigned stages (settled + live span). */
  workedMinutes: z.number().int(),
  /** Cumulative minutes the team was active but had no stage to work on. */
  idleMinutes: z.number().int(),
  /**
   * Hand-entered corrections, reported SEPARATELY from the measured figures
   * above so the two are never confused. Signed — negative takes time off. The
   * real figure is the automatic value plus its manual counterpart.
   */
  manualWorkedMinutes: z.number().int(),
  manualIdleMinutes: z.number().int(),
  manualBonusMinutes: z.number().int(),
  manualNote: z.string().nullable(),
  /**
   * Active members on the roster. 0 means the team can take work (the dispatcher
   * ignores staffing) but nobody can clock in, so the stage records no labor
   * hours and its punctuality bonus has no one to credit — the board flags this.
   */
  activeWorkerCount: z.number().int(),
  /**
   * Names of those same active members, so the board can show WHO is on a team
   * without a second request. Deliberately the same set `activeWorkerCount`
   * counts — a list that disagreed with the badge beside it would be worse than
   * no list at all.
   */
  workerNames: z.array(z.string()),
  /** The stage this team is currently working on, if any. */
  assignment: schedulerTeamAssignmentDto.nullable(),
  /**
   * Names of the active members on annual leave on the board's (virtual) day.
   * The crew works at the speed of the people present — nobody present, no work
   * that day.
   */
  onLeaveToday: z.array(z.string()).optional(),
  /**
   * The share of the roster present today, 0–100 — the crew's speed today.
   * Absent for a crew with no active roster (usually a collaborator, or an
   * unstaffed internal crew): leave never changes its speed. Any crew with
   * roster workers, whatever its kind, is scaled by the share present.
   */
  presenceTodayPercent: z.number().int().min(0).max(100).optional(),
  /**
   * The operation this collaborator is RESERVED for (a confirmed deadline
   * rescue): it takes no other work until that operation is done. Null when
   * not reserved.
   */
  reservedFor: z
    .object({
      stageId: z.string().cuid(),
      schedulerProjectId: z.string().cuid(),
      projectCode: z.string(),
      projectTitle: z.string(),
      stageCode: schedulerStageCodeEnum.or(z.string()),
    })
    .nullable()
    .optional(),
});
export type SchedulerTeamDto = z.infer<typeof schedulerTeamDto>;

/** One side of a preemption swap (the paused victim or the started challenger). */
export const schedulerProposalSideDto = z.object({
  stageId: z.string().cuid(),
  projectId: z.string().cuid(),
  projectCode: z.string(),
  projectTitle: z.string(),
  deadline: z.string().datetime().nullable(),
  deadlineBand: schedulerDeadlineBandEnum,
  slackMinutes: z.number().int().nullable(),
  workedMinutes: z.number().int(),
  estimatedMinutes: z.number().int(),
  /** Projected finish of the whole project if the swap is NOT applied. */
  projectedFinishBefore: z.string().datetime().nullable(),
  /** Projected finish of the whole project if the swap IS applied. */
  projectedFinishAfter: z.string().datetime().nullable(),
  lateByBefore: z.number().int().nullable(),
  lateByAfter: z.number().int().nullable(),
});
export type SchedulerProposalSideDto = z.infer<typeof schedulerProposalSideDto>;

export const schedulerAlertSuggestionDto = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('ADD_TEAM'),
    stageCode: schedulerStageCodeEnum,
    /** Total late-minutes saved across deadline projects if one team is added. */
    minutesSaved: z.number().int(),
    /** Deadlines that flip from missed to met if one team is added. */
    deadlinesRescued: z.number().int(),
  }),
  z.object({
    kind: z.literal('OVERTIME'),
    teamId: z.string().cuid().nullable(),
    teamName: z.string().nullable(),
    projectCode: z.string(),
    projectTitle: z.string(),
    overtimeMinutes: z.number().int(),
  }),
  /** Staff an empty team so its work is actually recorded against people. */
  z.object({
    kind: z.literal('ADD_WORKERS'),
    teamId: z.string().cuid(),
    teamName: z.string(),
    stageCode: schedulerStageCodeEnum,
  }),
  /**
   * The full cost of urgently starting a waiting at-risk project: it takes a
   * team from the MOST RELAXED project running at that stage (lowest priority
   * score — largest deadline slack, then lowest urgency and importance), which
   * goes back to the queue.
   *
   * Carries both sides projected twice — with and without the swap — so the
   * notification can answer "when would each project finish either way, what
   * does that save, and does the one going on waiting still make its deadline"
   * without the manager having to open the Start-now confirmation first.
   */
  z.object({
    kind: z.literal('PUT_ON_WAITING'),
    stageCode: schedulerStageCodeEnum,
    /** The team that changes hands. */
    teamId: z.string().cuid(),
    teamName: z.string(),
    /** The at-risk project that would start now. */
    started: schedulerProposalSideDto,
    /** The most relaxed project, which returns to the queue (progress kept). */
    paused: schedulerProposalSideDto,
    /** Work minutes the at-risk project's finish moves EARLIER. Null if unknown. */
    savedMinutes: z.number().int().nullable(),
    /** Work minutes the paused project's finish moves LATER. Null if unknown. */
    delayMinutes: z.number().int().nullable(),
  }),
  /** A team at the stage is idle — the at-risk project starts with no victim. */
  z.object({
    kind: z.literal('START_FREE_TEAM'),
    stageCode: schedulerStageCodeEnum,
    teamId: z.string().cuid(),
    teamName: z.string(),
  }),
]);
export type SchedulerAlertSuggestionDto = z.infer<typeof schedulerAlertSuggestionDto>;

// --------------------------------------------------------------------------- //
// Deadline rescue ("Recomandare pentru termen")                               //
// --------------------------------------------------------------------------- //
//
// For every project projected past its deadline (a PROJECT_AT_RISK alert) the
// scheduler works out ONE recommendation that gets it done before the deadline,
// or as soon as possible after it, without making any other deadline project
// later or leaving any project without a crew. It climbs a ladder and stops at
// the first rung that is enough:
//
//   1. CREWS — own crews: one more crew on an operation now (JOIN_NOW), one
//      that joins when it frees up (PLANNED), or one moved over from a project
//      that can afford it (MOVE_NOW);
//   2. COLLABORATOR — an external collaborator reserved for Proiectare,
//      Asamblare or Montaj (`schedulerCollaboratorStageCodes`);
//   3. SECOND_SHIFT — the last resort: "Schimbul 2", the smallest one (4–8h a
//      day) that does the job.
//
// Leave is not a rung: workers on annual leave are simply not counted, in the
// recommendation as everywhere else in the scheduler.
//
// Nothing is applied on its own. The plan rides on the risk alert
// (`schedulerAlertDto.rescue`); the manager previews it (`what-if` with
// `rescue`) and confirms the steps they tick (`POST rescue/:id/apply`), which
// re-checks them against a fresh snapshot before writing anything.

const rescueIso = z.string().datetime();
/** A whole calendar day, `YYYY-MM-DD` (UTC). */
const rescueCalendarDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD.');
const rescueId = z.string().cuid();

/** The most steps one recommendation — or one confirmed subset of it — carries. */
export const SCHEDULER_RESCUE_MAX_STEPS = 6;

/**
 * How an own crew is put on the operation: JOIN_NOW — it is idle and joins the
 * running operation straight away; PLANNED — it joins (or takes the operation,
 * if it is first) as soon as it frees up; MOVE_NOW — it leaves another
 * project's running operation for this one now.
 */
export const schedulerRescueCrewModeEnum = z.enum(['JOIN_NOW', 'PLANNED', 'MOVE_NOW']);
export type SchedulerRescueCrewMode = z.infer<typeof schedulerRescueCrewModeEnum>;

/**
 * READY — a recommendation exists. NONE — nothing helps (see the reason).
 * PENDING — not worked out yet on this request (the search is budgeted per
 * request); the web refetches while any plan is PENDING.
 */
export const schedulerRescueStatusEnum = z.enum(['READY', 'NONE', 'PENDING']);
export type SchedulerRescueStatus = z.infer<typeof schedulerRescueStatusEnum>;

/** ON_TIME — the deadline is met; REDUCED — still late, but as little as it can be. */
export const schedulerRescueOutcomeEnum = z.enum(['ON_TIME', 'REDUCED']);
export type SchedulerRescueOutcome = z.infer<typeof schedulerRescueOutcomeEnum>;

/** The highest rung of the ladder the recommendation had to use. */
export const schedulerRescueRungEnum = z.enum(['CREWS', 'COLLABORATOR', 'SECOND_SHIFT']);
export type SchedulerRescueRung = z.infer<typeof schedulerRescueRungEnum>;

/**
 * NO_HARMLESS_OPTION — every option that helps makes another deadline project
 * later (or leaves one without a crew), or none gives it a knowable finish.
 * NO_CREW_AT_STAGE — an operation has no active crew and cannot take a
 * collaborator. HELD_STAGE — an operation of a project that has already run is
 * on waiting (a manager's pause, "Reia" lifts it), so its finish cannot be
 * projected at all. NOT_STARTED — the project has never run and its first
 * operation waits for its estimated start ("Pornește", or a new "Începere
 * estimată", lets it in).
 */
export const schedulerRescueNoneReasonEnum = z.enum([
  'NO_HARMLESS_OPTION',
  'NO_CREW_AT_STAGE',
  'HELD_STAGE',
  'NOT_STARTED',
]);
export type SchedulerRescueNoneReason = z.infer<typeof schedulerRescueNoneReasonEnum>;

/**
 * Why a set of steps cannot be previewed or applied as sent. Every code names
 * the step's operation and crew where it has one (see
 * `schedulerRescueProblemDto`); HARMS_OTHER_PROJECT comes once per project that
 * would get later.
 */
export const schedulerRescueProblemEnum = z.enum([
  /** The project is no longer late nor blocked — nothing to rescue. */
  'NOT_AT_RISK',
  'STAGE_NOT_FOUND',
  /** The operation is done (or, for JOIN_NOW, not running). */
  'STAGE_CLOSED',
  /** The operation is on waiting. */
  'STAGE_HELD',
  'DUPLICATE_STEP',
  /** Inactive, not internal, does not cover the operation, reserved, or already planned. */
  'TEAM_NOT_ELIGIBLE',
  /** JOIN_NOW: the crew is on a running operation. */
  'TEAM_BUSY',
  /** The crew is on leave on the day it would start. */
  'TEAM_ABSENT',
  /** MOVE_NOW: the crew is no longer on the operation it would be moved from. */
  'TEAM_SOURCE_CHANGED',
  'NOT_A_COLLABORATOR',
  /** Collaborators only on Proiectare, Asamblare or Montaj. */
  'COLLABORATOR_STAGE_NOT_ALLOWED',
  /** The operation already has a collaborator reserved. */
  'COLLABORATOR_ALREADY_RESERVED',
  /** A new collaborator's name is already taken by another crew. */
  'NAME_TAKEN',
  /** The second shift asked for is not above the operation's current one. */
  'SHIFT_NOT_HIGHER',
  /** The shifted operation would have no internal crew — collaborators take no shift. */
  'NO_INTERNAL_CREW',
  /**
   * A Montaj already released would get further past its half-way point before
   * its painting is done (workshop rule 2) — `stageId` is that Montaj.
   */
  'PAINTING_GATE',
  'HARMS_OTHER_PROJECT',
  /** The steps do not bring the finish any earlier. */
  'NO_GAIN',
]);
export type SchedulerRescueProblem = z.infer<typeof schedulerRescueProblemEnum>;

// ---- inputs ----------------------------------------------------------------

/**
 * One step of a recommendation, as the web sends it back to preview or apply.
 *
 * COLLABORATOR `collaborator`: an existing COLLABORATOR crew (`teamId`), a new
 * one to create (`create`, the name optional — the next free one otherwise), or
 * null for the generic "a collaborator" the recommendation proposes. Null is
 * accepted by the preview only: applying needs a real crew
 * (`schedulerApplyRescueRequest`).
 */
export const schedulerRescueStepInput = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('CREW'),
      stageId: rescueId,
      teamId: rescueId,
      mode: schedulerRescueCrewModeEnum,
      /** MOVE_NOW only: the running operation the crew leaves. */
      fromStageId: rescueId.nullable().default(null),
    })
    .strict(),
  z
    .object({
      kind: z.literal('COLLABORATOR'),
      stageId: rescueId,
      /** null = the generic collaborator (preview only). */
      collaborator: z
        .union([
          z.object({ teamId: rescueId }).strict(),
          z
            .object({
              create: z.object({ name: z.string().trim().min(1).max(50).optional() }).strict(),
            })
            .strict(),
        ])
        .nullable()
        .default(null),
    })
    .strict(),
  z
    .object({
      kind: z.literal('SECOND_SHIFT'),
      stageId: rescueId,
      minutesPerDay: schedulerSecondShiftMinutes,
    })
    .strict(),
]);
/** What a caller sends — `fromStageId` / `collaborator` may be left out. */
export type SchedulerRescueStepInput = z.input<typeof schedulerRescueStepInput>;
/** A step as the API reads it, defaults applied. */
export type SchedulerRescueStepRequest = z.infer<typeof schedulerRescueStepInput>;

/**
 * The steps of one recommendation, or the subset of them the manager ticked.
 * Only the shape is checked here; whether they still make sense against the
 * board is the engine's call, answered with `schedulerRescueProblemEnum` codes.
 */
export const schedulerRescueSteps = z
  .array(schedulerRescueStepInput)
  .min(1)
  .max(SCHEDULER_RESCUE_MAX_STEPS)
  .superRefine((steps, ctx) => {
    const refuse = (index: number, field: string, message: string): void => {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, field], message });
    };
    const seen = new Set<string>();
    const crewTeamIds = new Set<string>();
    const collaboratorStageIds = new Set<string>();
    let shifts = 0;
    steps.forEach((step, index) => {
      const teamId =
        step.kind === 'CREW'
          ? step.teamId
          : step.kind === 'COLLABORATOR' && step.collaborator && 'teamId' in step.collaborator
            ? step.collaborator.teamId
            : '';
      const key = `${step.kind}|${step.stageId}|${teamId}`;
      if (seen.has(key)) {
        refuse(index, 'stageId', 'The same step is listed twice.');
        return;
      }
      seen.add(key);
      if (step.kind === 'CREW') {
        // The source operation is what a move is checked against at apply time,
        // so it is required exactly when the crew is moved — and meaningless
        // otherwise, where a stray one would only hide a client bug.
        if ((step.mode === 'MOVE_NOW') !== (step.fromStageId !== null)) {
          refuse(
            index,
            'fromStageId',
            'A crew moved now (MOVE_NOW) names the operation it leaves; the other modes name none.',
          );
        }
        if (crewTeamIds.has(step.teamId)) {
          refuse(index, 'teamId', 'A crew can be put on one operation only.');
        }
        crewTeamIds.add(step.teamId);
      } else if (step.kind === 'COLLABORATOR') {
        if (collaboratorStageIds.has(step.stageId)) {
          refuse(index, 'stageId', 'An operation takes one collaborator.');
        }
        collaboratorStageIds.add(step.stageId);
      } else {
        shifts += 1;
        if (shifts > 1) refuse(index, 'kind', 'A recommendation has at most one second shift.');
      }
    });
  });

/**
 * "Aplică": the ticked steps of a recommendation, applied together. The
 * project is the route's `:schedulerProjectId`. Every collaborator step must
 * name a real crew — an existing one or one to create — since there is no
 * generic collaborator to write down.
 */
export const schedulerApplyRescueRequest = z
  .object({ steps: schedulerRescueSteps })
  // Explicit: .refine() wraps this in ZodEffects, which the global pipe does
  // not strict-upgrade.
  .strict()
  .refine(
    (value) =>
      value.steps.every((step) => step.kind !== 'COLLABORATOR' || step.collaborator !== null),
    { message: 'Pick or create a collaborator.', path: ['steps'] },
  );
export type SchedulerApplyRescueRequest = z.infer<typeof schedulerApplyRescueRequest>;
export type SchedulerApplyRescueRequestInput = z.input<typeof schedulerApplyRescueRequest>;

// ---- outputs ---------------------------------------------------------------

/** Where one project ends up: its projected finish, how late, and whether blocked. */
export const schedulerRescueFinishDto = z.object({
  /** null when blocked (an operation has no crew at all). */
  projectedFinishAt: rescueIso.nullable(),
  /** Work minutes past the deadline; null when on time or without a deadline. */
  lateByMinutes: z.number().int().nullable(),
  blocked: z.boolean(),
});
export type SchedulerRescueFinishDto = z.infer<typeof schedulerRescueFinishDto>;

/**
 * MOVE_NOW: the operation the crew leaves, and what that does to its project —
 * the crew was a reinforcement (REMOVED), another crew there carries on
 * (PROMOTED, `promotedTeamName`), or the operation goes back to waiting
 * (REQUEUED, progress kept).
 */
export const schedulerRescueMoveSourceDto = z.object({
  stageId: rescueId,
  stageCode: schedulerStageCodeEnum.or(z.string()),
  schedulerProjectId: rescueId,
  projectCode: z.string(),
  projectTitle: z.string(),
  deadline: rescueIso.nullable(),
  /** The crew led the operation (rather than reinforcing it). */
  wasLead: z.boolean(),
  outcome: z.enum(['REMOVED', 'PROMOTED', 'REQUEUED']),
  promotedTeamName: z.string().nullable(),
  finishBefore: rescueIso.nullable(),
  finishAfter: rescueIso.nullable(),
  lateByBefore: z.number().int().nullable(),
  lateByAfter: z.number().int().nullable(),
});
export type SchedulerRescueMoveSourceDto = z.infer<typeof schedulerRescueMoveSourceDto>;

/** What every step says about the operation it acts on. */
const schedulerRescueStepBase = {
  stageId: rescueId,
  stageCode: schedulerStageCodeEnum.or(z.string()),
  stageStatus: schedulerStageStatusEnum,
};

/**
 * One step of a recommendation, resolved for display.
 *
 * `joinsAt` is when the crew is expected on the operation (now for JOIN_NOW and
 * MOVE_NOW); `leads` is true when it is expected to take the operation rather
 * than reinforce it — a planned crew free before anyone else, or a collaborator
 * on an operation that has not started.
 */
export const schedulerRescueStepDto = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('CREW'),
    ...schedulerRescueStepBase,
    teamId: rescueId,
    teamName: z.string(),
    /** The crew's home operation — differs from the stage's for a cross-skilled crew. */
    teamStageCode: schedulerStageCodeEnum.or(z.string()),
    mode: schedulerRescueCrewModeEnum,
    fromStageId: rescueId.nullable(),
    /** MOVE_NOW only. */
    from: schedulerRescueMoveSourceDto.nullable(),
    joinsAt: rescueIso.nullable(),
    leads: z.boolean(),
    /**
     * The crew that would lead the operation anyway, planned so the step after
     * it ADDS a crew instead of replacing the lead — true only in a search's
     * recommendation, so a one-line summary names the step that makes the
     * difference. Optional only so older fixtures stay valid; the API always
     * sends it.
     */
    anchor: z.boolean().optional(),
  }),
  z.object({
    kind: z.literal('COLLABORATOR'),
    ...schedulerRescueStepBase,
    stageCode: schedulerCollaboratorStageCodeEnum,
    /** null = the generic "a collaborator" — the manager picks one on apply. */
    teamName: z.string().nullable(),
    joinsAt: rescueIso.nullable(),
    leads: z.boolean(),
  }),
  z.object({
    kind: z.literal('SECOND_SHIFT'),
    ...schedulerRescueStepBase,
    /** The proposed shift: the smallest that does the job, else the most (480). */
    minutesPerDay: schedulerSecondShiftMinutes,
    /** The smallest shift that meets the deadline; null when none does. */
    meetsDeadlineAt: schedulerSecondShiftMinutes.nullable(),
    /** The INTERNAL crews on the operation — the ones who would work it. */
    teams: z.array(z.object({ id: rescueId, name: z.string() })),
  }),
]);
export type SchedulerRescueStepDto = z.infer<typeof schedulerRescueStepDto>;

/**
 * Another project the recommendation makes finish later. One with a deadline
 * only ever moves within it: never made late, nor later past it than it
 * already was. One without a deadline may move freely, and says so.
 */
export const schedulerRescueAffectedDto = z.object({
  schedulerProjectId: rescueId,
  projectCode: z.string(),
  projectTitle: z.string(),
  deadline: rescueIso.nullable(),
  finishBefore: rescueIso.nullable(),
  finishAfter: rescueIso.nullable(),
  /** Work minutes its finish moves later. */
  delayMinutes: z.number().int().positive(),
  lateByBefore: z.number().int().nullable(),
  lateByAfter: z.number().int().nullable(),
});
export type SchedulerRescueAffectedDto = z.infer<typeof schedulerRescueAffectedDto>;

/**
 * Leave the recommendation took into account: a crew of the project working
 * short-handed (`present` of `roster`), or not at all (`present` 0), over a
 * run of days inside one of its operations.
 */
export const schedulerRescueLeaveDto = z.object({
  teamId: rescueId,
  teamName: z.string(),
  stageId: rescueId,
  stageCode: schedulerStageCodeEnum.or(z.string()),
  /** Both inclusive. */
  from: rescueCalendarDate,
  to: rescueCalendarDate,
  present: z.number().int().nonnegative(),
  roster: z.number().int().positive(),
  /** Who is on leave. */
  workerNames: z.array(z.string()),
});
export type SchedulerRescueLeaveDto = z.infer<typeof schedulerRescueLeaveDto>;

/**
 * The recommendation for one at-risk project. `before` is the board as it is,
 * `after` the board with every step applied (READY only). `fingerprint`
 * identifies the recommendation by its steps — not by their numbers — so a
 * dismissed notification resurfaces when a DIFFERENT recommendation appears;
 * '' while PENDING.
 */
export const schedulerRescuePlanDto = z.object({
  schedulerProjectId: rescueId,
  status: schedulerRescueStatusEnum,
  outcome: schedulerRescueOutcomeEnum.nullable(),
  rung: schedulerRescueRungEnum.nullable(),
  noneReason: schedulerRescueNoneReasonEnum.nullable(),
  /** NO_CREW_AT_STAGE / HELD_STAGE / NOT_STARTED: the operation in question. */
  noneStageCode: schedulerStageCodeEnum.nullable(),
  fingerprint: z.string(),
  deadline: rescueIso.nullable(),
  before: schedulerRescueFinishDto,
  after: schedulerRescueFinishDto.nullable(),
  /** Work minutes the project's finish moves earlier; null unless READY. */
  savedMinutes: z.number().int().nullable(),
  steps: z.array(schedulerRescueStepDto).max(SCHEDULER_RESCUE_MAX_STEPS),
  /** The operations the steps act on, in step order — where the board shows the recommendation. */
  targetStageIds: z.array(rescueId),
  /** Other projects made later, the most delayed first (at most 5). */
  affected: z.array(schedulerRescueAffectedDto).max(5),
  /** Leave taken into account (at most 5). */
  leave: z.array(schedulerRescueLeaveDto).max(5),
});
export type SchedulerRescuePlanDto = z.infer<typeof schedulerRescuePlanDto>;

/** One reason a set of steps was refused (also `details.problems` of SCHEDULER_RESCUE_REJECTED). */
export const schedulerRescueProblemDto = z.object({
  code: schedulerRescueProblemEnum,
  stageId: rescueId.nullable(),
  teamId: rescueId.nullable(),
  /** HARMS_OTHER_PROJECT: the project that would get later. */
  schedulerProjectId: rescueId.nullable(),
  projectTitle: z.string().nullable(),
});
export type SchedulerRescueProblemDto = z.infer<typeof schedulerRescueProblemDto>;

/**
 * A what-if run of a set of steps (the apply dialog's live preview): what they
 * would do, and why they cannot be applied if `ok` is false.
 */
export const schedulerRescueEvaluationDto = z.object({
  schedulerProjectId: rescueId,
  ok: z.boolean(),
  problems: z.array(schedulerRescueProblemDto),
  steps: z.array(schedulerRescueStepDto),
  before: schedulerRescueFinishDto,
  after: schedulerRescueFinishDto.nullable(),
  savedMinutes: z.number().int().nullable(),
  meetsDeadline: z.boolean(),
  affected: z.array(schedulerRescueAffectedDto),
});
export type SchedulerRescueEvaluationDto = z.infer<typeof schedulerRescueEvaluationDto>;

export const schedulerAlertDto = z.object({
  /**
   * Deterministic id, e.g. "risk:<schedulerProjectId>", "overload:<stageCode>"
   * or "unstaffed:<teamId>".
   */
  id: z.string(),
  /**
   * TEAM_UNSTAFFED — an active team with no members is running a stage (or is
   * about to be handed one). Work advances but no hours or pay are recorded.
   */
  type: z.enum(['PROJECT_AT_RISK', 'STAGE_OVERLOAD', 'TEAM_UNSTAFFED']),
  severity: z.enum(['WARNING', 'CRITICAL']),
  stageCode: schedulerStageCodeEnum.nullable(),
  /** The team the alert is about (TEAM_UNSTAFFED); null for the other types. */
  teamId: z.string().cuid().nullable(),
  teamName: z.string().nullable(),
  projectId: z.string().cuid().nullable(),
  projectCode: z.string().nullable(),
  projectTitle: z.string().nullable(),
  deadline: z.string().datetime().nullable(),
  projectedFinishAt: z.string().datetime().nullable(),
  lateByMinutes: z.number().int().nullable(),
  demandMinutes: z.number().int().nullable(),
  capacityMinutes: z.number().int().nullable(),
  suggestions: z.array(schedulerAlertSuggestionDto),
  /**
   * PROJECT_AT_RISK: the scheduler row the alert is about — `projectId` is the
   * PROJECT's id, which a warranty intervention shares with its production run.
   * Null on the other types.
   */
  schedulerProjectId: z.string().cuid().nullable().optional(),
  /**
   * PROJECT_AT_RISK: the deadline recommendation for the project (see
   * "Deadline rescue"). Null on the other types, and absent for roles that
   * see no alerts at all.
   */
  rescue: schedulerRescuePlanDto.nullable().optional(),
});
export type SchedulerAlertDto = z.infer<typeof schedulerAlertDto>;

/**
 * A disruptive rearrangement the engine recommends but does NOT apply on its
 * own (outside the simulator): pause the running `paused` stage (progress and
 * queue seniority kept) and start `started` on the freed team. Applied via
 * confirm; hidden via dismiss until its urgency escalates.
 */
export const schedulerProposalDto = z.object({
  /** Deterministic id: "preempt:<startedStageId>:<pausedStageId>". */
  id: z.string(),
  type: z.literal('PREEMPT'),
  stageCode: schedulerStageCodeEnum.or(z.string()),
  teamId: z.string().cuid(),
  teamName: z.string(),
  paused: schedulerProposalSideDto,
  started: schedulerProposalSideDto,
});
export type SchedulerProposalDto = z.infer<typeof schedulerProposalDto>;

/** What every crew offered for a waiting operation carries, whatever its state. */
const schedulerTeamOptionBase = {
  teamId: z.string().cuid(),
  teamName: z.string(),
  /** The crew's home operation — differs from the stage's for a cross-skilled crew. */
  teamStageCode: schedulerStageCodeEnum.or(z.string()),
};

/**
 * One crew that can do a waiting operation, and what handing it the work costs.
 *
 * - FREE: it is idle and starts straight away.
 * - SWAP: it leads another project's running operation; taking it puts that
 *   project on the waiting list (progress and seniority kept). `proposal`
 *   carries both projects' finish with and without the swap.
 * - BUSY: it cannot be taken — it is only reinforcing someone else's job
 *   (pausing that job would not free it cleanly), it is already working on
 *   this same project, or the whole crew is on leave on the day it would start
 *   (ON_LEAVE, with an empty `projectTitle`).
 */
export const schedulerTeamOptionDto = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('FREE'), ...schedulerTeamOptionBase }),
  z.object({ kind: z.literal('SWAP'), ...schedulerTeamOptionBase, proposal: schedulerProposalDto }),
  z.object({
    kind: z.literal('BUSY'),
    ...schedulerTeamOptionBase,
    reason: z.enum(['REINFORCING', 'SAME_PROJECT', 'ON_LEAVE']),
    projectTitle: z.string(),
  }),
]);
export type SchedulerTeamOptionDto = z.infer<typeof schedulerTeamOptionDto>;

/**
 * Every crew that could take one waiting operation, home crews first.
 *
 * `recommendedTeamId` is the scheduler's suggestion: a free crew when there is
 * one, otherwise the crew on the MOST RELAXED running project — the one whose
 * deadline can best afford the wait. Null when no crew can be taken at all.
 */
export const schedulerTeamChoiceDto = z.object({
  stageId: z.string().cuid(),
  stageCode: schedulerStageCodeEnum.or(z.string()),
  projectTitle: z.string(),
  recommendedTeamId: z.string().cuid().nullable(),
  options: z.array(schedulerTeamOptionDto),
});
export type SchedulerTeamChoiceDto = z.infer<typeof schedulerTeamChoiceDto>;

/**
 * Preview for the manual "Start now" action on a queued stage: the crews that
 * could take it, for the manager to pick from. "Start now" only starts the
 * project; a stage with no team at all is surfaced by the stage overload
 * notification ("add a team"), not here.
 */
export const schedulerStartNowPreviewResponse = schedulerTeamChoiceDto;
export type SchedulerStartNowPreviewResponse = z.infer<typeof schedulerStartNowPreviewResponse>;

/**
 * A crew picked off a preview. `pausedStageId` is the running operation the
 * manager agreed to put on waiting (null for a free crew): if the crew's
 * situation changed since the preview — it went free, or is now on another
 * project — the pick is refused rather than pausing something nobody saw.
 */
const schedulerTeamPick = {
  teamId: z.string().cuid(),
  pausedStageId: z.string().cuid().nullable(),
};

/**
 * Both actions took no body before a crew could be picked, and a request with
 * no body reaches validation as `undefined`, not `{}` — so a client that still
 * sends nothing (an old tab, a script) keeps meaning "no pick" instead of
 * failing with a 400. Strict inside, since the preprocess wrapper hides the
 * object from the validation pipe's own strict-upgrade.
 */
const noBodyMeansEmpty = (value: unknown): unknown => value ?? {};

/** "Start now" with a picked crew; omitted, the recommended one is used. */
export const schedulerStartNowRequest = z.preprocess(
  noBodyMeansEmpty,
  z.object({ pick: z.object(schedulerTeamPick).strict().optional() }).strict(),
);
export type SchedulerStartNowRequest = z.infer<typeof schedulerStartNowRequest>;

/**
 * "Done", optionally naming the crew that takes the project's next waiting
 * operation (`stageId`, as the preview showed it). Omitted, the next operation
 * joins its queue and the dispatcher staffs it as usual.
 */
/** One day of hours "Done" enters in the timesheet: "Salvează pontaj?" answered DA. */
export const schedulerCompleteTimesheetEntry = z
  .object({
    workerId: z.string().cuid(),
    /** `YYYY-MM-DD`, Romania's. */
    date: schedulerCalendarDay,
    hours: z
      .number()
      .gt(0)
      .max(24)
      .refine((hours) => Math.round(hours * 60) > 0, {
        message: 'An entry needs at least one minute of work.',
      }),
    /** The part past the day's program is not paid, as on any hand entry. */
    unpaidOvertime: z.boolean().optional(),
  })
  .strict();
export type SchedulerCompleteTimesheetEntry = z.infer<typeof schedulerCompleteTimesheetEntry>;

export const schedulerCompleteStageRequest = z.preprocess(
  noBodyMeansEmpty,
  z
    .object({
      next: z
        .object({ stageId: z.string().cuid(), ...schedulerTeamPick })
        .strict()
        .optional(),
      /** Absent: "Salvează pontaj?" answered NU, and the timesheet is untouched. */
      timesheet: z.array(schedulerCompleteTimesheetEntry).min(1).max(200).optional(),
      /** Crews it frees that wait instead of taking what comes next. */
      waitingTeamIds: z.array(z.string().cuid()).max(20).optional(),
    })
    .strict(),
);
export type SchedulerCompleteStageRequest = z.infer<typeof schedulerCompleteStageRequest>;

/**
 * A crew "Gata" or "Pune în așteptare" frees, with the work it would take next if
 * left to itself: the question "Începe X cu echipa Y, sau pune echipa Y în
 * așteptare". One with nothing to take is not listed, so nothing is asked.
 */
export const schedulerFreedTeamDto = z.object({
  teamId: z.string(),
  teamName: z.string(),
  next: z.object({
    stageId: z.string(),
    stageCode: schedulerStageCodeEnum.or(z.string()),
    projectTitle: z.string(),
  }),
});
export type SchedulerFreedTeamDto = z.infer<typeof schedulerFreedTeamDto>;

/**
 * What marking an operation done would lead to: whether it finishes the
 * project, and otherwise the crews that could take the operation that becomes
 * next — null when nothing new is waiting (e.g. the next one already runs, or
 * still waits on another operation).
 */
export const schedulerCompletePreviewResponse = z.object({
  completesProject: z.boolean(),
  next: schedulerTeamChoiceDto.nullable(),
  /** The crews it frees that have something to go on to. */
  freedTeams: z.array(schedulerFreedTeamDto),
});
export type SchedulerCompletePreviewResponse = z.infer<typeof schedulerCompletePreviewResponse>;

/** "Pune în așteptare" on a running operation. */
export const schedulerPauseStageRequest = z.preprocess(
  noBodyMeansEmpty,
  z
    .object({
      /** Crews it frees that wait too, instead of taking what comes next. */
      waitingTeamIds: z.array(z.string().cuid()).max(20).optional(),
    })
    .strict(),
);
export type SchedulerPauseStageRequest = z.infer<typeof schedulerPauseStageRequest>;

/** What "Pune în așteptare" would free, for its confirmation. */
export const schedulerPausePreviewResponse = z.object({
  freedTeams: z.array(schedulerFreedTeamDto),
});
export type SchedulerPausePreviewResponse = z.infer<typeof schedulerPausePreviewResponse>;

/**
 * What the "Salvează pontaj?" question of "Done" offers: the total worked on the
 * operation and, for each person on it, what they have in the timesheet, what is
 * left, and the days the hours the flow counted would go on.
 */
export const schedulerCompleteTimesheetResponse = z.object({
  /** Everything worked on the operation, not capped at the estimate. */
  workedMinutes: z.number().int(),
  /** The part it arrived with at enrollment: never counted as left to enter. */
  seededMinutes: z.number().int(),
  estimatedMinutes: z.number().int(),
  /**
   * "Left" counts from the estimate: the flow has nothing to say, because it no
   * longer counts by itself or the run fell outside anyone's program.
   */
  fromEstimate: z.boolean(),
  /** `YYYY-MM-DD`, Romania's, on the scheduler's clock: the latest day to enter. */
  today: z.string(),
  /** False for a crew with nobody to enter hours for: a collaborator's. */
  hasPeople: z.boolean(),
  workers: z.array(
    z.object({
      workerId: z.string(),
      workerName: z.string(),
      /** In the timesheet on this project (this run of it), and on this operation. */
      projectMinutes: z.number().int(),
      stageMinutes: z.number().int(),
      remainingMinutes: z.number().int(),
      /** Their weekly program, to say when a day passes it. */
      program: z.array(
        z.object({
          weekday: z.number().int(),
          startMinutes: z.number().int().nullable(),
          endMinutes: z.number().int().nullable(),
        }),
      ),
      /** Only hours the flow really counted, on their program over the run. */
      suggested: z.array(z.object({ date: z.string(), minutes: z.number().int() })),
    }),
  ),
});
export type SchedulerCompleteTimesheetResponse = z.infer<typeof schedulerCompleteTimesheetResponse>;

export const schedulerBoardResponse = z.object({
  /** Virtual now (real time + simulated offset). */
  now: z.string().datetime(),
  clock: z.object({ offsetMinutes: z.number().int() }),
  /** The working calendar every date on the board is laid out on. */
  calendar: schedulerCalendarDto,
  /**
   * Whether the flow counts work by itself. Optional only for older fixtures —
   * the API always sends it; absent reads as on, the original behaviour.
   */
  autoProgress: schedulerAutoProgressDto.optional(),
  teams: z.array(schedulerTeamDto),
  projects: z.array(schedulerProjectDto),
  alerts: z.array(schedulerAlertDto),
  /** Pending disruptive rearrangements awaiting confirmation. */
  proposals: z.array(schedulerProposalDto),
  /**
   * Project ids that have a PRODUCTION scheduler row the actor can see —
   * including expired warranties no longer in `projects` — so the enroll picker
   * never offers them.
   */
  enrolledProjectIds: z.array(z.string()),
});
export type SchedulerBoardResponse = z.infer<typeof schedulerBoardResponse>;

/**
 * Start lags in HOURS at the API edge, keyed by operation: "this operation may
 * start N hours after the previous one started". Null clears a lag. Partial —
 * an operation not named keeps whatever it has (on enroll: no lag).
 *
 * Built from the code tuple like every other per-operation record, so a stage
 * added tomorrow is accepted here without anyone remembering to list it.
 */
export const schedulerStageStartLagHours = z
  .object(
    Object.fromEntries(
      schedulerStageCodes.map((code) => [code, estimateHours.nullable()]),
    ) as Record<SchedulerStageCode, z.ZodNullable<typeof estimateHours>>,
  )
  .partial()
  // Explicit for the same reason as on enroll: .superRefine() wraps this in
  // ZodEffects, which the global pipe does not strict-upgrade.
  .strict()
  // The workshop rules (see schedulerMinStartLagMinutes): refused rather than
  // silently raised, so the form never shows a lag the engine does not apply.
  .superRefine((value, ctx) => {
    for (const code of schedulerStageCodes) {
      const hours = value[code];
      if (hours === null || hours === undefined) continue;
      const floor = schedulerMinStartLagMinutes[code];
      if (floor !== undefined && hours * 60 < floor) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [code],
          message: `${code} may start no sooner than ${floor / 60}h after the previous operation started.`,
        });
      }
    }
  });
export type SchedulerStageStartLagHours = z.infer<typeof schedulerStageStartLagHours>;

/**
 * The operation each one depends on, keyed by operation: "Cantare de la start
 * Circular", "CNC de la start Circular" — so CNC runs beside Cantare instead
 * of after it. Null puts an operation back on the workshop rule (the previous
 * operation); an operation not named keeps what it has (on enroll: the rule).
 *
 * Only an operation BEFORE it in the pipeline: the order stays fixed.
 */
export const schedulerStageDependencies = z
  .object(
    Object.fromEntries(
      schedulerStageCodes.map((code) => [code, schedulerStageCodeEnum.nullable()]),
    ) as Record<SchedulerStageCode, z.ZodNullable<typeof schedulerStageCodeEnum>>,
  )
  .partial()
  // Explicit for the same reason as on the lags: .superRefine() wraps this in
  // ZodEffects, which the global pipe does not strict-upgrade.
  .strict()
  .superRefine((value, ctx) => {
    for (const code of schedulerStageCodes) {
      const on = value[code];
      if (on === null || on === undefined) continue;
      if (schedulerStageCodes.indexOf(on) >= schedulerStageCodes.indexOf(code)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [code],
          message: `${code} can only depend on an operation before it.`,
        });
      }
    }
  });
export type SchedulerStageDependencies = z.infer<typeof schedulerStageDependencies>;

/** The most crews one operation may be designated to. */
export const SCHEDULER_MAX_DESIGNATED_TEAMS = 12;

const designatedTeamIds = z
  .array(z.string().cuid())
  .max(SCHEDULER_MAX_DESIGNATED_TEAMS)
  .refine((ids) => new Set(ids).size === ids.length, {
    message: 'A crew is designated on an operation once.',
  });

/**
 * The crews each operation is RESERVED for, keyed by operation — for the first
 * shift and the second alike (a crew's shift is its own: `schedulerTeamDto.shift`).
 * [] clears an operation's designations; an operation not named keeps whatever
 * it has (on enroll: none).
 *
 * The designation is FIRM for the set — the dispatcher, the projection and the
 * manual pickers all narrow to the named crews, so the operation waits for one
 * of them rather than falling back to another — and a promise: a designated
 * crew goes onto it as soon as it is free of the work it is on, leading it
 * while it has no lead and joining it once it runs. That is the whole point of
 * designating them, but it does mean a reserved operation can sit in the queue
 * while a capable crew stands idle, which is why every key is optional and the
 * default is none.
 *
 * Built from the code tuple like the other per-operation records, so a stage
 * added tomorrow is accepted here without anyone remembering to list it.
 */
export const schedulerStageDesignatedTeams = z
  .object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, designatedTeamIds])) as Record<
      SchedulerStageCode,
      typeof designatedTeamIds
    >,
  )
  .partial();
export type SchedulerStageDesignatedTeams = z.infer<typeof schedulerStageDesignatedTeams>;

export const enrollSchedulerProjectRequest = z
  .object({
    projectId: z.string().cuid(),
    importance: schedulerPriorityLevelEnum.default('NORMAL'),
    urgency: schedulerPriorityLevelEnum.default('NORMAL'),
    estimatedHours: schedulerStageEstimates,
    /**
     * The operation this project actually begins at — for work arriving
     * part-finished (e.g. panels already cut and edged elsewhere). Every earlier
     * stage is still created, stored DONE with `completedAt: null`; that null is
     * what marks it *skipped* rather than genuinely worked, and it is how the
     * start can later be moved back without resurrecting real history.
     */
    startStageCode: schedulerStageCodeEnum.default('DESIGN'),
    /**
     * The operations this project does NOT undergo — a job with no painting, or
     * one delivered flat-packed with no installation.
     *
     * Distinct from `startStageCode`, which can only skip a PREFIX of the
     * pipeline. These are individual operations anywhere in it, and they are
     * stored the same way a skipped one is (DONE with a null `completedAt`) plus
     * an `excludedAt` marker that keeps a later start move from reviving them.
     */
    excludedStageCodes: z.array(schedulerStageCodeEnum).default([]),
    /**
     * When each operation may start relative to the previous one — "Cantare 4h
     * after Circular started" — so the two overlap instead of queueing end to
     * end. Operations not named wait for the previous one to finish — except
     * those with a workshop floor (schedulerMinStartLagMinutes), which get the
     * floor. Name one with null to make it wait for the finish instead.
     */
    startLagHours: schedulerStageStartLagHours.default({}),
    /**
     * The operation each one depends on, when not the previous one — the lag
     * then counts from that operation's start. Operations not named follow the
     * workshop rule.
     */
    dependsOnStageCodes: schedulerStageDependencies.default({}),
    /**
     * Optionally, the crews each operation is reserved for — they come onto it
     * as they finish what they are on. Refused (400 VALIDATION_FAILED) when a
     * crew is unknown, inactive, or does not cover that operation — a
     * reservation nothing can satisfy would simply park the project. Operations
     * not named are dispatched as they always have been.
     */
    designatedTeamIds: schedulerStageDesignatedTeams.default({}),
    /**
     * Of each operation's estimate, how much is already done but unrecorded.
     * The remainder is what still has to be scheduled.
     */
    workedHours: schedulerStageWorkedHours.default({}),
    /**
     * Manual estimated start ("Începere estimată"). A planning input only — it
     * never affects dispatch or the projection; the estimated finish is derived
     * from it in the UI. Null or omitted leaves it unset.
     */
    plannedStartAt: z.string().datetime().nullable().optional(),
  })
  // .strict() is explicit because .refine() below wraps this in ZodEffects,
  // which the global pipe does not strict-upgrade — a typoed key would then be
  // stripped instead of rejected.
  .strict()
  .refine(
    (value) =>
      schedulerStageCodes.every(
        (code) => (value.workedHours[code] ?? 0) <= value.estimatedHours[code],
      ),
    {
      // Otherwise the projection would clamp the remainder at zero and complete
      // the operation the instant it was enrolled, which reads as the scheduler
      // losing the work rather than as the typo it is.
      message: 'Hours already worked cannot exceed the estimate for that operation.',
      path: ['workedHours'],
    },
  );
export type EnrollSchedulerProjectRequest = z.infer<typeof enrollSchedulerProjectRequest>;

/** Same shape as enroll — dry-run projection without persisting anything. */
export const previewSchedulerEnrollmentRequest = enrollSchedulerProjectRequest;
export type PreviewSchedulerEnrollmentRequest = z.infer<typeof previewSchedulerEnrollmentRequest>;

export const schedulerEnrollmentPreviewResponse = z.object({
  /** null when the project is blocked (an operation has no team → unknown finish). */
  projectedFinishAt: z.string().datetime().nullable(),
  deadline: z.string().datetime().nullable(),
  /** null when the project has no deadline. */
  meetsDeadline: z.boolean().nullable(),
  deadlineBand: schedulerDeadlineBandEnum,
  stages: z.array(
    z.object({
      stageCode: schedulerStageCodeEnum,
      estimatedMinutes: z.number().int(),
      /** null for operations that can't be scheduled (no team upstream/here). */
      projectedStartAt: z.string().datetime().nullable(),
      projectedFinishAt: z.string().datetime().nullable(),
    }),
  ),
});
export type SchedulerEnrollmentPreviewResponse = z.infer<typeof schedulerEnrollmentPreviewResponse>;

/** How many hypothetical crews one stage may be given in a what-if run. */
export const SCHEDULER_WHAT_IF_MAX_TEAMS = 5;
const extraTeamCount = z.number().int().min(1).max(SCHEDULER_WHAT_IF_MAX_TEAMS);

/**
 * Hypothetical "Schimbul 2" crews on one operation, simulation only: `teams`
 * crews that work `minutesPerDay` after the 7h day, every working day.
 *
 * A second-shift crew carries on the work of one of the operation's own crews
 * — the relay a real second shift is, at the same workstation: paired with the
 * operation's INTERNAL crews first (by name), then with the run's hypothetical
 * extra crews, then with the internal crews that also cover the operation. A
 * crew with a partner gets (420 + m) / 420 as much done there per day — what a
 * real "Schimbul 2" of m minutes gives it (the larger of the two counts when
 * the operation already has one). A second-shift crew left without a partner
 * works on its own, at m / 420 of a day crew. Collaborators are never paired:
 * they take no shift.
 */
export const schedulerSimulatedSecondShift = z
  .object({ teams: extraTeamCount, minutesPerDay: schedulerSecondShiftMinutes })
  .strict();
export type SchedulerSimulatedSecondShift = z.infer<typeof schedulerSimulatedSecondShift>;

/** Hypothetical extra crews per operation, e.g. `{ CUTTING: 2 }`. */
const extraTeamsByStage = z
  .object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, extraTeamCount])) as Record<
      SchedulerStageCode,
      typeof extraTeamCount
    >,
  )
  .partial()
  .strict();

/** Hypothetical second-shift crews per operation, e.g. `{ CNC: { teams: 1, minutesPerDay: 480 } }`. */
const secondShiftTeamsByStage = z
  .object(
    Object.fromEntries(
      schedulerStageCodes.map((code) => [code, schedulerSimulatedSecondShift]),
    ) as Record<SchedulerStageCode, typeof schedulerSimulatedSecondShift>,
  )
  .partial()
  .strict();

/**
 * What-if projection: the whole board re-projected with hypothetical extra
 * teams, so a "what would adding a team change?" can be SHOWN (the Gantt's
 * before/after lanes) rather than only summed up in an alert's minutes-saved.
 */
/** Money as Project Organization bounds it (`moneyValue`), in RON. */
const simulatedMoney = z.number().min(0).max(9_999_999_999.99);

/**
 * Co-work in a project simulation: the operations whose crew, when it has
 * nothing else waiting, does not sit idle until the operation before finishes —
 * it starts {@link SCHEDULER_CO_WORK_LAG_MINUTES} after that one STARTED.
 *
 * Pairs of the pipeline as the simulated project actually runs it (previous →
 * next), not of the stage tuple: an operation left out hands its successor a
 * new predecessor, and that pair no longer co-works.
 */
export const schedulerCoWorkPairs = [
  ['CUTTING', 'EDGING'],
  ['EDGING', 'CNC'],
  ['ASD', 'INSTALLATION'],
] as const satisfies readonly (readonly [SchedulerStageCode, SchedulerStageCode])[];
export const SCHEDULER_CO_WORK_LAG_MINUTES = 60;

/**
 * A project that does not exist yet, simulated on the Gantt: a name, its value,
 * optionally its materials, and when it would start and/or has to be done.
 *
 * Its hours come from Project Organization's own rule — value minus materials
 * minus the default profit, commission and additional percentages, divided by
 * the average hourly rate, split by the global time shares — computed on the
 * server, which is where the rates live.
 *
 * Both dates are DATE-ONLY values carried as UTC midnight of the picked day,
 * the convention `Project.deadline` is stored in. The start is a floor ("not
 * before"), the finish is the deadline; without a start the project competes
 * from now.
 */
export const schedulerSimulatedProjectRequest = z
  .object({
    name: z.string().trim().min(1).max(120),
    value: simulatedMoney.refine((value) => value > 0, { message: 'Value must be positive.' }),
    materialsCost: simulatedMoney.default(0),
    plannedStartAt: z.string().datetime().nullable().optional(),
    deadline: z.string().datetime().nullable().optional(),
    /**
     * Hours typed per operation, replacing the time-share split for exactly the
     * operations named — the work the global shares give no time (a 0%
     * Proiectare or Vopsitorie) or a figure the manager knows better. Every
     * operation left out keeps its share of the labour hours. Bounded like an
     * enrollment's estimate.
     */
    stageHours: schedulerStageEstimates.partial().strict().optional(),
    /**
     * Let the simulated project co-work ({@link schedulerCoWorkPairs}): a crew
     * with nothing else waiting starts an hour after the operation before it
     * started, instead of waiting for it to finish. Only the simulated project —
     * the existing ones keep the start lags stored on them — and never at the
     * expense of work already waiting for that crew. Off unless asked for, so a
     * body without it projects exactly what it always did.
     */
    coWork: z.boolean().default(false),
  })
  .strict();
export type SchedulerSimulatedProjectRequest = z.infer<typeof schedulerSimulatedProjectRequest>;
/** What a caller may send — defaults still optional. */
export type SchedulerSimulatedProjectRequestInput = z.input<
  typeof schedulerSimulatedProjectRequest
>;

export const schedulerWhatIfRequest = z
  .object({
    /** Hypothetical extra teams per operation, e.g. `{ CUTTING: 1 }`. */
    extraTeams: extraTeamsByStage.default({}),
    /**
     * Hypothetical second-shift crews per operation
     * ({@link schedulerSimulatedSecondShift}). Off unless asked for, so a body
     * without it projects exactly what it always did.
     */
    secondShiftTeams: secondShiftTeamsByStage.default({}),
    /** One extra, hypothetical project competing for the same crews. */
    virtualProject: schedulerSimulatedProjectRequest.optional(),
    /**
     * A deadline recommendation's steps (all of them, or the ones ticked in the
     * apply dialog), previewed exactly as applying them would play out. The
     * response then carries `rescue`, the evaluation. The steps are always
     * sent — the server never searches here — and a generic collaborator
     * (`collaborator: null`) is allowed, unlike on apply.
     */
    rescue: z
      .object({ schedulerProjectId: z.string().cuid(), steps: schedulerRescueSteps })
      .strict()
      .optional(),
  })
  .strict()
  // .strict() is explicit for the same reason as elsewhere in this file:
  // .refine() wraps the schema in ZodEffects, which the global pipe does not
  // strict-upgrade.
  .refine(
    (value) =>
      Object.values(value.extraTeams).some((count) => count !== undefined) ||
      Object.values(value.secondShiftTeams).some((shift) => shift !== undefined) ||
      value.virtualProject !== undefined ||
      value.rescue !== undefined,
    { message: 'At least one stage or a simulated project is required.' },
  )
  // A recommendation is measured against the board as it is; mixed with
  // hypothetical crews or a simulated project, its "before" would not be the
  // board any more, and neither would the harm it is checked for.
  .refine(
    (value) =>
      value.rescue === undefined ||
      (Object.values(value.extraTeams).every((count) => count === undefined) &&
        Object.values(value.secondShiftTeams).every((shift) => shift === undefined) &&
        value.virtualProject === undefined),
    { message: 'A recommendation is previewed on its own.', path: ['rescue'] },
  );
export type SchedulerWhatIfRequest = z.infer<typeof schedulerWhatIfRequest>;
/**
 * What a caller sends. `z.infer` is the OUTPUT type, where a defaulted key is
 * required; the web builds bodies against this one.
 */
export type SchedulerWhatIfRequestInput = z.input<typeof schedulerWhatIfRequest>;

/** Why a simulated project could not be projected (details.reason of SCHEDULER_SIMULATION_INVALID). */
export const schedulerSimulationInvalidReasons = [
  'NO_RATE',
  'VALUE_TOO_LOW',
  'NO_HOURS',
  'STAGE_TOO_LONG',
] as const;
export type SchedulerSimulationInvalidReason = (typeof schedulerSimulationInvalidReasons)[number];

export const schedulerWhatIfStageDto = z.object({
  stageId: z.string().cuid(),
  schedulerProjectId: z.string().cuid(),
  stageCode: schedulerStageCodeEnum.or(z.string()),
  projectedStartAt: z.string().datetime(),
  projectedFinishAt: z.string().datetime(),
  /** null while the stage lands on one of the hypothetical teams. */
  teamName: z.string().nullable(),
  /** True when the hypothetical extra team is the one taking this stage. */
  virtualTeam: z.boolean(),
  /**
   * Every crew the scenario puts on this stage, the lead first. A collaborator
   * a recommendation would bring in is flagged by `virtualCollaborator`; the
   * generic one has no name of its own, so the Gantt labels it instead.
   */
  teamNames: z.array(z.string()).optional(),
  /**
   * True when a collaborator the recommendation would bring in — the generic
   * one or one still to be created — works this stage in the scenario.
   */
  virtualCollaborator: z.boolean().optional(),
  /**
   * True when the stage lands on a hypothetical second-shift crew working on
   * its own (one beyond the crews there are to pair with — see
   * {@link schedulerSimulatedSecondShift}).
   */
  virtualShift: z.boolean().optional(),
  /**
   * The simulated second shift one of the stage's crews works it with, in
   * minutes a day; null/absent when none of them has a second-shift partner.
   */
  simulatedShiftMinutes: z.number().int().nullable().optional(),
});
export type SchedulerWhatIfStageDto = z.infer<typeof schedulerWhatIfStageDto>;

/** An operation along a project's chain, named by its stage. */
const staffingHoldStageCode = schedulerStageCodeEnum.or(z.string());

/**
 * What the LAST operation projected at a stage waits on, so "the work here is
 * done no sooner" names the stage that actually holds it:
 *
 *   - RUNNING — it is already under way here; new crews do not join an
 *     operation that has started;
 *   - WAITS — it waits here for a crew (its designated one, when `designated`);
 *   - START — it starts as soon as its project lets it, and one crew does it;
 *   - UPSTREAM — another operation of its project releases it late
 *     (`releasedBy`: its predecessor finishing, a start lag, or painting for
 *     Montaj). `bottleneck` follows that chain back to where the project
 *     actually loses its time — "the problematic stage": the longest wait for
 *     a crew along it (an hour or more, WAITS); else the operation already
 *     under way at its top (RUNNING); else the one with the most work
 *     (LONGEST).
 */
export const schedulerStaffingHoldDto = z.object({
  /** The project whose operation finishes last here. */
  projectTitle: z.string().nullable(),
  kind: z.enum(['RUNNING', 'WAITS', 'START', 'UPSTREAM']),
  /** WAITS: work-minutes it waits here, and whether for its designated crew. */
  waitMinutes: z.number().int(),
  designated: z.boolean(),
  releasedBy: z
    .object({ stageCode: staffingHoldStageCode, via: z.enum(['FINISH', 'LAG', 'GATE']) })
    .nullable(),
  bottleneck: z
    .object({
      stageCode: staffingHoldStageCode,
      kind: z.enum(['WAITS', 'RUNNING', 'LONGEST']),
      /** WAITS: work-minutes it waits there for a crew, and whether for its designated one. */
      waitMinutes: z.number().int(),
      designated: z.boolean(),
      /** Work-minutes that operation runs (what is left of it, for one under way). */
      workMinutes: z.number().int(),
      /** When that operation is projected to finish. */
      finishAt: z.string().datetime(),
    })
    .nullable(),
});
export type SchedulerStaffingHoldDto = z.infer<typeof schedulerStaffingHoldDto>;

/**
 * What a team scenario does to one operation, board-wide — "how adding these
 * crews streamlines the flow", in numbers: the crews and the daily hours the
 * operation works with, the work it has left, how long that work keeps it busy
 * and how long operations wait there for a crew. Before is the board as it is
 * (the Gantt's lower lane), after the scenario (the upper one).
 *
 * The daily hours count the 7h day of every crew whose home operation it is,
 * plus the simulated second shift; a second shift already set on a single
 * project's stage, crews lent from other operations and leave are left out —
 * the dates (`clearsBefore` / `clearsAfter`, the waits) come from the
 * projection itself and count all of it.
 */
export const schedulerStaffingStageDto = z.object({
  stageCode: schedulerStageCodeEnum,
  /** Active crews whose home operation this is (collaborators included), not reserved elsewhere. */
  teams: z.number().int(),
  /** The scenario's hypothetical crews in the 7h day. */
  extraTeams: z.number().int(),
  /** The scenario's hypothetical second-shift crews, and their shift (null without). */
  secondShiftTeams: z.number().int(),
  secondShiftMinutes: z.number().int().nullable(),
  /** Work-minutes a day the operation's crews put in — before, and with the scenario. */
  dailyMinutesBefore: z.number().int(),
  dailyMinutesAfter: z.number().int(),
  /** Work left at the operation over every active project (a simulated project's included). */
  demandMinutes: z.number().int(),
  /** Work-minutes operations wait for a crew here, summed — before / after. */
  waitMinutesBefore: z.number().int(),
  waitMinutesAfter: z.number().int(),
  /** Operations that wait at least half an hour for a crew here — before / after. */
  waitingBefore: z.number().int(),
  waitingAfter: z.number().int(),
  /** When the last operation projected here finishes: the work it has now, cleared. */
  clearsBefore: z.string().datetime().nullable(),
  clearsAfter: z.string().datetime().nullable(),
  /**
   * Why the work here ends when it does with the scenario (`clearsAfter`) —
   * what one crew more HERE could not change ({@link schedulerStaffingHoldDto}).
   * Null when nothing is projected here.
   */
  heldBy: schedulerStaffingHoldDto.nullable().optional(),
});
export type SchedulerStaffingStageDto = z.infer<typeof schedulerStaffingStageDto>;

export const schedulerWhatIfResponse = z.object({
  now: z.string().datetime(),
  /** Only not-DONE stages appear — finished work is not re-projected. */
  stages: z.array(schedulerWhatIfStageDto),
  projects: z.array(
    z.object({
      schedulerProjectId: z.string().cuid(),
      /** null when the project is blocked (an operation still has no team). */
      projectedFinishAt: z.string().datetime().nullable(),
      /** Work minutes past the deadline in this scenario; null when on time. */
      lateByMinutes: z.number().int().nullable(),
      /**
       * The same project in the same snapshot WITHOUT the hypothetical — no
       * extra teams, no simulated project. Compare against these, never against
       * the board's own dates: the board was projected at a different instant,
       * and a minute of drift would read as a delay.
       */
      baselineProjectedFinishAt: z.string().datetime().nullable(),
      baselineLateByMinutes: z.number().int().nullable(),
    }),
  ),
  /**
   * The baseline's own stage dates — filled only for a project simulation,
   * where the snapshot differs from the board's (deferred projects take part).
   * The Gantt draws the "before" lane from these so it agrees with the
   * baseline finishes; for a team-only what-if the board's dates are that lane.
   */
  baselineStages: z.array(
    z.object({
      stageId: z.string().cuid(),
      projectedStartAt: z.string().datetime(),
      projectedFinishAt: z.string().datetime(),
    }),
  ),
  /** The simulated project, when one was sent; stages keyed by code, in pipeline order. */
  virtualProject: z
    .object({
      name: z.string(),
      /** Labour hours before the split — Project Organization's `laborHours`. */
      laborHours: z.number(),
      /** The global time shares' total; the split is not renormalised when it is not 100. */
      sharesTotal: z.number(),
      plannedStartAt: z.string().datetime().nullable(),
      deadline: z.string().datetime().nullable(),
      /** null when blocked (an operation it needs has no team at all). */
      projectedFinishAt: z.string().datetime().nullable(),
      lateByMinutes: z.number().int().nullable(),
      deadlineBand: schedulerDeadlineBandEnum,
      stages: z.array(
        z.object({
          stageCode: schedulerStageCodeEnum,
          estimatedMinutes: z.number().int(),
          projectedStartAt: z.string().datetime().nullable(),
          projectedFinishAt: z.string().datetime().nullable(),
          teamName: z.string().nullable(),
          virtualTeam: z.boolean(),
          /** As on `schedulerWhatIfStageDto`: a second-shift crew of its own takes it. */
          virtualShift: z.boolean().optional(),
        }),
      ),
    })
    .nullable(),
  /**
   * The evaluation of the recommendation's steps, when `rescue` was sent: the
   * before/after of the project, the other projects it would make later, and —
   * when `ok` is false — why it cannot be applied. Null otherwise.
   */
  rescue: schedulerRescueEvaluationDto.nullable().optional(),
  /**
   * When hypothetical crews were sent (`extraTeams`, `secondShiftTeams`): what
   * they do to each operation that has work or one of them, in pipeline order
   * ({@link schedulerStaffingStageDto}). Before is the response's baseline.
   */
  stageStats: z.array(schedulerStaffingStageDto).optional(),
});
export type SchedulerWhatIfResponse = z.infer<typeof schedulerWhatIfResponse>;

// --------------------------------------------------------------------------- //
// Staffing recommendation ("Recomandă echipele")                               //
// --------------------------------------------------------------------------- //
//
// How many crews the shop would need, and on which operations, for every
// deadline project to be on time — the Gantt simulation's "Recomandă". A pure
// engine search over the same hypothetical crews the what-if takes, one
// variant per kind:
//
//   - DAY — more crews in the 7h day;
//   - SECOND_SHIFT — crews in a second shift ({@link schedulerSimulatedSecondShift}),
//     each at the smallest shift (4–8h a day) that does as well as 8h;
//   - COMBINED — both, only when neither alone gets every project on time.
//
// Each adds one crew at a time where it gains the most, and only while a crew
// is worth it — a deadline met, a project dated again, or one project a whole
// working day less late; drops every crew the end result can do without; and
// explains itself: the crews in the order they help, what each one wins, and
// what the whole plan does to every operation and every project at risk.
// Nothing is persisted — the manager loads a variant into the simulation and
// decides.

/** Request: optionally with the project being simulated, which then takes part in every figure. */
export const schedulerStaffingRequest = z
  .object({ virtualProject: schedulerSimulatedProjectRequest.optional() })
  .strict();
export type SchedulerStaffingRequest = z.infer<typeof schedulerStaffingRequest>;
export type SchedulerStaffingRequestInput = z.input<typeof schedulerStaffingRequest>;

export const schedulerStaffingVariantKindEnum = z.enum(['DAY', 'SECOND_SHIFT', 'COMBINED']);
export type SchedulerStaffingVariantKind = z.infer<typeof schedulerStaffingVariantKindEnum>;

/** One hypothetical crew of a plan: in the 7h day, or in a second shift. */
export const schedulerStaffingMoveKindEnum = z.enum(['DAY', 'SECOND_SHIFT']);
export type SchedulerStaffingMoveKind = z.infer<typeof schedulerStaffingMoveKindEnum>;

/**
 * ALL_ON_TIME — every deadline project on time; IMPROVED — fewer late or less
 * lateness, not all of it; NO_GAIN — no crew helps (no `steps`).
 */
export const schedulerStaffingOutcomeEnum = z.enum(['ALL_ON_TIME', 'IMPROVED', 'NO_GAIN']);
export type SchedulerStaffingOutcome = z.infer<typeof schedulerStaffingOutcomeEnum>;

/**
 * The deadline side of a world, compared in this order: deadline projects with
 * no knowable finish (an operation without a crew), deadline projects late,
 * and their lateness summed (work-minutes).
 */
export const schedulerStaffingScoreDto = z.object({
  blocked: z.number().int(),
  late: z.number().int(),
  lateMinutes: z.number().int(),
});
export type SchedulerStaffingScoreDto = z.infer<typeof schedulerStaffingScoreDto>;

/** A project a plan names. The simulated one goes by `virtual:project`, with code null. */
export const schedulerStaffingProjectRefDto = z.object({
  schedulerProjectId: z.string(),
  projectCode: z.string().nullable(),
  projectTitle: z.string().nullable(),
});
export type SchedulerStaffingProjectRefDto = z.infer<typeof schedulerStaffingProjectRefDto>;

/**
 * One crew of a plan, in the order they help: the operation, which crew it is
 * there (`count` — 2 for the second one), and what the plan gains with it —
 * the score before and after it, and the deadline projects it brings in on
 * time. Measured on the final plan, so the steps add up to its result.
 */
export const schedulerStaffingStepDto = z.object({
  kind: schedulerStaffingMoveKindEnum,
  stageCode: schedulerStageCodeEnum,
  count: z.number().int(),
  /** SECOND_SHIFT: the plan's shift on this operation; null for a day crew. */
  minutesPerDay: schedulerSecondShiftMinutes.nullable(),
  before: schedulerStaffingScoreDto,
  after: schedulerStaffingScoreDto,
  rescued: z.array(schedulerStaffingProjectRefDto),
});
export type SchedulerStaffingStepDto = z.infer<typeof schedulerStaffingStepDto>;

/** A project's finish without and with the plan; a null finish means it cannot be dated (blocked). */
export const schedulerStaffingProjectDto = schedulerStaffingProjectRefDto.extend({
  deadline: z.string().datetime().nullable(),
  finishBefore: z.string().datetime().nullable(),
  finishAfter: z.string().datetime().nullable(),
  /** Work-minutes past the deadline; null when on time (or without a deadline). */
  lateMinutesBefore: z.number().int().nullable(),
  lateMinutesAfter: z.number().int().nullable(),
  /** The project being simulated. */
  simulated: z.boolean(),
});
export type SchedulerStaffingProjectDto = z.infer<typeof schedulerStaffingProjectDto>;

export const schedulerStaffingVariantDto = z.object({
  kind: schedulerStaffingVariantKindEnum,
  outcome: schedulerStaffingOutcomeEnum,
  /** The plan as a what-if takes it — load it into the simulation as is. */
  extraTeams: extraTeamsByStage,
  secondShiftTeams: secondShiftTeamsByStage,
  /** Day crews plus second-shift crews. */
  totalTeams: z.number().int(),
  after: schedulerStaffingScoreDto,
  steps: z.array(schedulerStaffingStepDto),
  /** Every operation with work or with a crew of the plan, in pipeline order. */
  stages: z.array(schedulerStaffingStageDto),
  /**
   * The deadline projects at risk without the plan (late or not datable), and
   * every project the plan makes finish an hour or more later — rescued first,
   * then still late, then the ones it delays.
   */
  projects: z.array(schedulerStaffingProjectDto),
});
export type SchedulerStaffingVariantDto = z.infer<typeof schedulerStaffingVariantDto>;

export const schedulerStaffingResponse = z.object({
  now: z.string().datetime(),
  /** The board (with the simulated project, when one was sent) without any crew added. */
  before: schedulerStaffingScoreDto,
  /**
   * How many of the deadline projects at risk are already past their deadline:
   * no crew brings those in on time, only sooner.
   */
  overdue: z.number().int(),
  /** DAY, then SECOND_SHIFT, then COMBINED when it was needed. Empty when nothing is at risk. */
  variants: z.array(schedulerStaffingVariantDto),
  /**
   * The search hit its budget before it was done: the variants are the best it
   * found by then — still valid, maybe not the smallest.
   */
  truncated: z.boolean(),
  /** The simulated project's name when one was sent. */
  virtualProjectName: z.string().nullable(),
});
export type SchedulerStaffingResponse = z.infer<typeof schedulerStaffingResponse>;

/**
 * "Schimbul 2" in HOURS at the API edge, keyed by operation: 4 to 8 extra hours
 * a day on the half hour, stored as minutes (`schedulerSecondShiftMinutes`).
 * Null clears one; an operation not named keeps whatever it has.
 *
 * Built from the code tuple like the other per-operation records, so a stage
 * added tomorrow is accepted here without anyone remembering to list it.
 */
const secondShiftHours = z
  .number()
  .min(SCHEDULER_SECOND_SHIFT_MIN_MINUTES / 60)
  .max(SCHEDULER_SECOND_SHIFT_MAX_MINUTES / 60)
  .multipleOf(SCHEDULER_SECOND_SHIFT_STEP_MINUTES / 60);
export const schedulerStageSecondShiftHours = z
  .object(
    Object.fromEntries(
      schedulerStageCodes.map((code) => [code, secondShiftHours.nullable()]),
    ) as Record<SchedulerStageCode, z.ZodNullable<typeof secondShiftHours>>,
  )
  .partial()
  .strict();
export type SchedulerStageSecondShiftHours = z.infer<typeof schedulerStageSecondShiftHours>;

/**
 * "Redeschide": the hours an adjustment needs, keyed by the finished operation
 * reopened for it — above 0, on the minute, at most an operation's worth.
 * Built from the code tuple like the other per-operation records.
 */
const reopenHours = z
  .number()
  .gt(0)
  .max(SCHEDULER_MAX_STAGE_HOURS)
  .refine((hours) => Math.round(hours * 60) > 0, {
    message: 'A reopened operation needs at least one minute of work.',
  });
export const schedulerStageReopenHours = z
  .object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, reopenHours])) as Record<
      SchedulerStageCode,
      typeof reopenHours
    >,
  )
  .partial()
  .strict();
export type SchedulerStageReopenHours = z.infer<typeof schedulerStageReopenHours>;

export const updateSchedulerProjectRequest = z
  .object({
    importance: schedulerPriorityLevelEnum.optional(),
    urgency: schedulerPriorityLevelEnum.optional(),
    /**
     * Only the stages being changed; DONE stages are rejected server-side. On a
     * warranty intervention every value must be above 0 h (400
     * VALIDATION_FAILED), except for an operation excluded in the same request.
     */
    estimatedHours: schedulerStageEstimates.partial().optional(),
    /**
     * Correct the hours already worked on the operations named ("Ore lucrate"),
     * finished ones included. Replaces the engine's tally: a running operation
     * carries on from the new figure. A finished operation's punctuality bonus
     * is NOT recomputed — it was settled when it was completed. Refused (409
     * CONFLICT) on an operation the project does not perform — excluded, or
     * skipped by the start point. Never reaches attendance or pay: like the
     * hours seeded at enrollment, the figure is the engine's, not the payroll's.
     */
    workedHours: schedulerStageWorkedHours.optional(),
    /**
     * Reopen finished operations for an adjustment ("Redeschide"), each with the
     * hours it needs. The operation goes back into the pipeline keeping the
     * hours it has; its estimate becomes those plus the new ones, so the
     * punctuality bonus its "Gata" paid stays and the next "Gata" counts only
     * the adjustment. An operation that depends on its finish and has not
     * started waits for it again; one with a start lag counts from its start
     * and does not. Applied after `workedHours`, so a correction in the same
     * request is what the new estimate builds on. Refused (409 CONFLICT) on an
     * operation that is not finished — a skipped or excluded one comes back
     * through `startStageCode` or `excludedStageCodes`.
     */
    reopenStageHours: schedulerStageReopenHours.optional(),
    /**
     * "Mută proiectul în etapa respectivă", answered DA: the project goes back to
     * the first operation in `reopenStageHours`. It waits there in the queue, and
     * the unfinished operations after it stop, keeping their hours and freeing
     * their crews, until it is done. One that is finished stays finished. Brings a
     * project parked or waiting for its estimated start back onto the board. Needs
     * `reopenStageHours`, and no `startStageCode` in the same request.
     */
    moveToReopenedStage: z.literal(true).optional(),
    /**
     * Move where the project begins. An operation skipped forward that already
     * has recorded work is not skipped but closed with it: DONE, its hours
     * kept, no punctuality bonus — its crews are freed. Hours set to 0 by
     * `workedHours` in the same request skip it instead. Moving back only
     * revives stages that were skipped, never ones truly completed; one after
     * the new start that already has work keeps it and its place.
     */
    startStageCode: schedulerStageCodeEnum.optional(),
    /**
     * The complete set of operations this project does not undergo — sent whole
     * rather than as a delta, so excluding and re-including are one operation
     * and a concurrent edit cannot merge two partial intentions into a pipeline
     * nobody asked for.
     *
     * Re-including an operation puts it back to PENDING. Excluding one that
     * already has recorded work closes it with that work instead, as moving the
     * start past it does: the hours were really worked, and filing them under
     * "never performed" would rewrite a team's history. A finished one is
     * refused (409 CONFLICT).
     */
    excludedStageCodes: z.array(schedulerStageCodeEnum).optional(),
    /**
     * Start lags to change, in hours (null clears one). Only the operations
     * named are touched; one that has already started is refused.
     */
    startLagHours: schedulerStageStartLagHours.optional(),
    /**
     * What each named operation depends on (null: back to the previous one).
     * Only the operations named are touched; one that has already started is
     * refused, like its lag.
     */
    dependsOnStageCodes: schedulerStageDependencies.optional(),
    /**
     * Crew reservations to change: each operation named gets exactly the crews
     * listed ([] clears them); the others keep theirs. Changing them does NOT
     * take a crew off an operation it is working — the designation decides who
     * is put on it next — but a designated crew that is free goes onto it at
     * once.
     */
    designatedTeamIds: schedulerStageDesignatedTeams.optional(),
    /**
     * "Schimbul 2" to change, in hours per day (null clears one). Only the
     * operations named are touched; a DONE one is refused. The internal crews
     * on the operation work that much longer every day until it is done; a
     * running operation's progress so far is kept at the speed it had.
     */
    secondShiftHours: schedulerStageSecondShiftHours.optional(),
    /**
     * WARRANTY rows only (409 SCHEDULER_INTERVENTION_LOCKED otherwise): the
     * intervention's own deadline, ISO datetime, or null to clear.
     */
    interventionDeadline: z.string().datetime().nullable().optional(),
    /**
     * Manual estimated start ("Începere estimată"). A planning input only — it
     * never affects dispatch, queue order, or the projection. The estimated
     * finish is derived from it in the UI (start + remaining estimated work).
     * Pass an ISO datetime to set, or `null` to clear. On a warranty
     * intervention it cannot be later than both the warranty's end and now (409
     * SCHEDULER_INTERVENTION_LOCKED, START_AFTER_WARRANTY).
     */
    plannedStartAt: z.string().datetime().nullable().optional(),
  })
  // Explicit, for the same reason the enroll body carries one: the global pipe
  // only strict-upgrades a bare ZodObject, and the `.refine` below makes this a
  // ZodEffects. Without it a typo'd key is silently STRIPPED and the request
  // reports success having done nothing.
  .strict()
  .refine((v) => Object.values(v).some((value) => value !== undefined), {
    message: 'At least one field is required.',
  })
  .refine((v) => !v.moveToReopenedStage || (v.reopenStageHours && !v.startStageCode), {
    message: 'Moving the project needs an operation reopened, and no new start.',
    path: ['moveToReopenedStage'],
  });
export type UpdateSchedulerProjectRequest = z.infer<typeof updateSchedulerProjectRequest>;

const interventionHours = z.number().gt(0).max(SCHEDULER_MAX_STAGE_HOURS);

/** A key per SELECTED operation (E2); order is irrelevant — they always run in pipeline order. */
export const schedulerInterventionEstimates = z
  .object(
    Object.fromEntries(schedulerStageCodes.map((code) => [code, interventionHours])) as Record<
      SchedulerStageCode,
      typeof interventionHours
    >,
  )
  .partial()
  .strict()
  .refine(
    (v) => {
      const hours = Object.values(v).filter((h): h is number => h !== undefined);
      return hours.length > 0 && hours.every((h) => Math.round(h * 60) > 0);
    },
    { message: 'Select at least one operation, each with an estimate of at least one minute.' },
  );

export const reopenSchedulerProjectRequest = z
  .object({
    estimatedHours: schedulerInterventionEstimates,
    importance: schedulerPriorityLevelEnum.default('NORMAL'),
    urgency: schedulerPriorityLevelEnum.default('NORMAL'),
    /** The intervention's own deadline (D2); the project's original deadline never applies. */
    deadline: z.string().datetime().nullable().optional(),
    /**
     * Future (no later than the warranty's end): waits in "Proiecte în
     * programare"; empty or past: straight to the queues (E3).
     */
    plannedStartAt: z.string().datetime().nullable().optional(),
    notes: z.string().trim().max(2000).nullable().optional(),
  })
  .strict();
export type ReopenSchedulerProjectRequest = z.infer<typeof reopenSchedulerProjectRequest>;

export const schedulerHistoryStageOutcomeEnum = z.enum([
  'COMPLETED',
  'NOT_PERFORMED',
  'CANCELLED',
  'OPEN',
]);
export type SchedulerHistoryStageOutcome = z.infer<typeof schedulerHistoryStageOutcomeEnum>;

export const schedulerHistoryStageDto = z.object({
  stageCode: schedulerStageCodeEnum,
  status: schedulerStageStatusEnum,
  outcome: schedulerHistoryStageOutcomeEnum,
  estimatedMinutes: z.number().int(),
  workedMinutes: z.number().int(),
  startedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  /** Every team ever put on the stage (recorded names ∪ live teams), in join order. */
  teamNames: z.array(z.string()),
  /** Closed WorkSession minutes clocked on this cycle and operation. */
  attendanceMinutes: z.number().int(),
});
export type SchedulerHistoryStageDto = z.infer<typeof schedulerHistoryStageDto>;

export const schedulerHistoryCycleDto = z.object({
  schedulerProjectId: z.string().cuid(),
  kind: schedulerProjectKindEnum,
  interventionNumber: z.number().int().positive().nullable(),
  status: schedulerProjectStatusEnum,
  importance: schedulerPriorityLevelEnum,
  urgency: schedulerPriorityLevelEnum,
  /** PRODUCTION: Project.deadline; WARRANTY: the intervention's own. */
  deadline: z.string().datetime().nullable(),
  plannedStartAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  /** WARRANTY: openedAt (virtual clock); PRODUCTION: enrollment time. */
  openedAt: z.string().datetime(),
  openedByName: z.string().nullable(),
  firstStartedAt: z.string().datetime().nullable(),
  completedAt: z.string().datetime().nullable(),
  cancelledAt: z.string().datetime().nullable(),
  workedMinutes: z.number().int(),
  attendanceMinutes: z.number().int(),
  /** All seven, pipeline order. */
  stages: z.array(schedulerHistoryStageDto),
});
export type SchedulerHistoryCycleDto = z.infer<typeof schedulerHistoryCycleDto>;

export const schedulerProjectHistoryResponse = z.object({
  projectId: z.string().cuid(),
  projectCode: z.string(),
  projectTitle: z.string(),
  clientName: z.string(),
  warranty: schedulerWarrantyDto.nullable(),
  /** Production first, then interventions by number ascending. */
  cycles: z.array(schedulerHistoryCycleDto),
});
export type SchedulerProjectHistoryResponse = z.infer<typeof schedulerProjectHistoryResponse>;

// --------------------------------------------------------------------------- //
// Project attendance — who clocked what on each operation of one board row     //
// --------------------------------------------------------------------------- //

/** One timesheet entry behind a person's hours on an operation. */
export const schedulerAttendanceEntryDto = z.object({
  /** The session — what the timesheet's own edit and delete act on. */
  id: z.string(),
  startedAt: z.string().datetime(),
  /** null while the person is clocked in on it. */
  endedAt: z.string().datetime().nullable(),
  workedMinutes: z.number().int(),
  /** Typed in by a manager rather than clocked. */
  manual: z.boolean(),
});
export type SchedulerAttendanceEntryDto = z.infer<typeof schedulerAttendanceEntryDto>;

/** One person's hours on one operation of the row, from its first session on. */
export const schedulerAttendanceWorkerDto = z.object({
  workerId: z.string(),
  fullName: z.string(),
  /** The person's team today — how the timesheet groups them. */
  teamName: z.string(),
  /** Closed sessions only, exactly the minutes Pontaj lunar counts. */
  workedMinutes: z.number().int(),
  /** Romanian date (`YYYY-MM-DD`) of the last closed session; null with only an open one. */
  lastWorkedOn: z.string().nullable(),
  /** Clocked in on it right now; that session's minutes count once it is closed. */
  clockedIn: z.boolean(),
  /**
   * The entries behind the figure, newest first — an open one included. Optional
   * only for older fixtures; the API always sends it.
   */
  entries: z.array(schedulerAttendanceEntryDto).optional(),
});
export type SchedulerAttendanceWorkerDto = z.infer<typeof schedulerAttendanceWorkerDto>;

/**
 * One operation, read two ways: the flow's own tally ("Ore lucrate" on the
 * board) and the hours the people put on it in the timesheet. They are
 * different measures — the flow counts a crew's time while it sits on the
 * operation, the timesheet each person's — so they are shown side by side
 * rather than reconciled behind the reader's back.
 */
export const schedulerAttendanceStageDto = z.object({
  stageCode: schedulerStageCodeEnum,
  status: schedulerStageStatusEnum,
  /** False when skipped by the start point or excluded — stored DONE with no completion. */
  performed: z.boolean(),
  /** The flow's tally in crew-minutes, live and uncapped: what "Ore lucrate" shows and edits. */
  flowMinutes: z.number().int(),
  /** The timesheet's: the sum of `workers`. */
  workedMinutes: z.number().int(),
  /** Most hours first. */
  workers: z.array(schedulerAttendanceWorkerDto),
});
export type SchedulerAttendanceStageDto = z.infer<typeof schedulerAttendanceStageDto>;

/**
 * Everything clocked on one board row — the production run or a warranty
 * intervention — by operation and by person, beside the flow's tally. The same
 * sessions, under the same rules, as Pontaj lunar's project hours: closed ones
 * only, a session naming no intervention counting as production.
 */
export const schedulerProjectAttendanceResponse = z.object({
  schedulerProjectId: z.string().cuid(),
  projectId: z.string().cuid(),
  projectCode: z.string(),
  projectTitle: z.string(),
  clientName: z.string(),
  kind: schedulerProjectKindEnum,
  interventionNumber: z.number().int().positive().nullable(),
  /** All seven, pipeline order. */
  stages: z.array(schedulerAttendanceStageDto),
  /** Hours on the row with no operation recorded — sessions from before that was tracked. */
  unassigned: z.object({
    workedMinutes: z.number().int(),
    workers: z.array(schedulerAttendanceWorkerDto),
  }),
  totals: z.object({
    flowMinutes: z.number().int(),
    /** Every operation's plus the unassigned bucket. */
    workedMinutes: z.number().int(),
  }),
});
/**
 * What "Resetează orele" does to the timesheet entries on the project:
 *   KEEP   — they stay on it; only the flow goes back to 0;
 *   DETACH — they leave it: the hours stay with the people, under no project,
 *            so attendance and pay do not change;
 *   DELETE — they are deleted: the people lose those hours from the timesheet
 *            and from pay. Someone clocked in on it right now is detached
 *            instead — a running shift is stopped, not deleted.
 */
export const schedulerResetTimesheetModes = ['KEEP', 'DETACH', 'DELETE'] as const;
export const schedulerResetTimesheetModeEnum = z.enum(schedulerResetTimesheetModes);
export type SchedulerResetTimesheetMode = z.infer<typeof schedulerResetTimesheetModeEnum>;

export const resetSchedulerProjectHoursRequest = z
  .object({
    /** DETACH when left out — what a reset always did before the choice existed. */
    timesheet: schedulerResetTimesheetModeEnum.default('DETACH'),
  })
  .strict();
export type ResetSchedulerProjectHoursRequest = z.input<typeof resetSchedulerProjectHoursRequest>;

export type SchedulerProjectAttendanceResponse = z.infer<typeof schedulerProjectAttendanceResponse>;

/** A calendar day marked by the simulator, as a UTC `YYYY-MM-DD` string. */
const schedulerCalendarDate = schedulerCalendarDay;

/**
 * Working-days filter for a clock-advance — the simulator's, which the board no
 * longer offers (the shop's calendar is `schedulerCalendarDto`). Kept so an
 * advance can still be restricted to some days: on top of the saved calendar,
 * it skips the days it marks non-working. Default is "every weekday works,
 * weekends off"; `nonWorkingDates` carves out holidays and `workingDates`
 * re-enables a specific weekend day.
 */
export const schedulerWorkingCalendar = z.object({
  /** When true, Saturdays and Sundays are non-working unless listed in `workingDates`. */
  weekendsOff: z.boolean().default(true),
  /** Weekdays (or weekends) forced non-working — holidays/closures. */
  nonWorkingDates: z.array(schedulerCalendarDate).max(500).default([]),
  /** Dates forced working — overrides both the weekend default and `nonWorkingDates`. */
  workingDates: z.array(schedulerCalendarDate).max(500).default([]),
});
export type SchedulerWorkingCalendar = z.infer<typeof schedulerWorkingCalendar>;

export const advanceSchedulerClockRequest = z
  .object({
    hours: z.number().int().min(0).max(2160).optional(),
    days: z.number().int().min(0).max(180).optional(),
    /** Optional working-days calendar; when omitted, every day counts (legacy behavior). */
    calendar: schedulerWorkingCalendar.optional(),
  })
  .refine((v) => (v.hours ?? 0) + (v.days ?? 0) > 0, {
    message: 'Provide hours or days to advance.',
  });
export type AdvanceSchedulerClockRequest = z.infer<typeof advanceSchedulerClockRequest>;

export const createSchedulerTeamRequest = z.object({
  stageCode: schedulerStageCodeEnum,
  /** Omit to auto-generate the next name for the stage (P1, P2, …). */
  name: z.string().trim().min(1).max(50).optional(),
  /** Additional operations this crew covers; must not repeat `stageCode`. */
  extraStageCodes: z.array(schedulerStageCodeEnum).max(5).optional(),
  /** COLLABORATOR marks an external crew paid per project. */
  kind: schedulerTeamKindEnum.optional(),
});
export type CreateSchedulerTeamRequest = z.infer<typeof createSchedulerTeamRequest>;

export const updateSchedulerTeamRequest = z
  .object({
    name: z.string().trim().min(1).max(50).optional(),
    active: z.boolean().optional(),
    /** "Reia": out of the wait, and on to whatever is next. Set only by the question. */
    waiting: z.literal(false).optional(),
    /** Replaces the list wholesale; send [] to make the team single-skilled. */
    extraStageCodes: z.array(schedulerStageCodeEnum).max(5).optional(),
    kind: schedulerTeamKindEnum.optional(),
  })
  .refine((v) => Object.values(v).some((value) => value !== undefined), {
    message: 'At least one field is required.',
  });
export type UpdateSchedulerTeamRequest = z.infer<typeof updateSchedulerTeamRequest>;

/**
 * Put another team on a running stage so it finishes proportionally sooner.
 * Manager-driven only — the dispatcher assigns exactly one team per stage, so it
 * can never sweep a stage's whole bench into a single urgent project.
 */
export const assignStageTeamRequest = z.object({
  teamId: z.string().cuid(),
});
export type AssignStageTeamRequest = z.infer<typeof assignStageTeamRequest>;

// --------------------------------------------------------------------------- //
// Collaborators — external crews paid per project                             //
// --------------------------------------------------------------------------- //

const money = z.string().regex(/^-?\d+(\.\d{1,2})?$/);

/**
 * One collaborator engaged on one project.
 *
 * `days` is DERIVED from the operations this collaborator actually worked on
 * the project — first start to last finish — so it can never disagree with the
 * board. It counts calendar days inclusive, which is how a per-day figure is
 * normally settled with an external crew.
 */
export const collaboratorEngagementDto = z.object({
  id: z.string(),
  teamId: z.string(),
  teamName: z.string(),
  projectId: z.string(),
  projectCode: z.string(),
  projectTitle: z.string(),
  agreedAmount: money,
  dayRate: money.nullable(),
  currency: z.enum(['RON', 'EUR', 'USD']),
  notes: z.string().nullable(),
  /** When their first operation on this project started; null before it does. */
  startedAt: z.string().datetime().nullable(),
  /** When their last operation finished; null while still running. */
  finishedAt: z.string().datetime().nullable(),
  /** Inclusive calendar days from start to finish (to today while running). */
  days: z.number().int(),
  /** True while at least one of their operations is still unfinished. */
  running: z.boolean(),
  /** days x dayRate, for comparison against agreedAmount; null without a rate. */
  computedFromDays: money.nullable(),
  /** The operations they were engaged on, in pipeline order. */
  stageCodes: z.array(z.string()),
});
export type CollaboratorEngagementDto = z.infer<typeof collaboratorEngagementDto>;

export const collaboratorEngagementsResponse = z.object({
  engagements: z.array(collaboratorEngagementDto),
});
export type CollaboratorEngagementsResponse = z.infer<typeof collaboratorEngagementsResponse>;

export const upsertCollaboratorEngagementRequest = z.object({
  teamId: z.string().cuid(),
  projectId: z.string().cuid(),
  agreedAmount: money.default('0.00'),
  dayRate: money.nullable().optional(),
  currency: z.enum(['RON', 'EUR', 'USD']).default('RON'),
  notes: z.string().trim().max(2000).nullable().optional(),
});
export type UpsertCollaboratorEngagementRequest = z.infer<
  typeof upsertCollaboratorEngagementRequest
>;

/**
 * Hand-entered corrections to one team's hours.
 *
 * Deliberately additive rather than an override: the scheduler's own count is
 * never touched, so a correction can always be read apart from what was
 * measured, and cleared by sending zeros.
 */
export const updateTeamManualHoursRequest = z
  .object({
    manualWorkedMinutes: z.number().int().min(-100_000).max(100_000).optional(),
    manualIdleMinutes: z.number().int().min(-100_000).max(100_000).optional(),
    manualBonusMinutes: z.number().int().min(-100_000).max(100_000).optional(),
    manualNote: z.string().trim().max(500).nullable().optional(),
  })
  .refine((v) => Object.values(v).some((value) => value !== undefined), {
    message: 'At least one field is required.',
  });
export type UpdateTeamManualHoursRequest = z.infer<typeof updateTeamManualHoursRequest>;
